import { toast } from 'react-toastify';
import LocalDataService from './LocalDataService';
import { API_BASE, ENDPOINTS } from '../config/api';

class ApiService {
  constructor() {
    this.apiBase = API_BASE;
  }

  static _authRedirecting = false;

  // V3 模型接口使用 sk- 开头的 API Key。仅在内存中缓存，避免把密钥写进画布快照。
  static _v3ApiKeyCache = { ownerToken: null, key: null };

  static resetV3ApiKeyCache() {
    ApiService._v3ApiKeyCache = { ownerToken: null, key: null };
  }

  _handleAuthExpired() {
    if (ApiService._authRedirecting) return;
    ApiService._authRedirecting = true;
    LocalDataService.set_user_data(null);
    toast.info('登录已过期，请重新登录');
    setTimeout(() => {
      const redirectPath = window.location.hash.replace('#', '') || '/';
      window.dispatchEvent(new CustomEvent('canvas:auth-required', {
        detail: { redirectPath }
      }));
      ApiService._authRedirecting = false;
    }, 1000);
  }

  _isAuthError(error) {
    if (error.status === 401) return true;
    if (error.status === 500 && error.data) {
      try {
        const raw = typeof error.data === 'string' ? error.data : JSON.stringify(error.data);
        if (raw.includes('过期') && raw.includes('重新登')) return true;
      } catch (_) {}
    }
    return false;
  }

  // 通用的请求方法
  async request (url, method, data, options = {}) {
    const silent = Boolean(options && options.silent);
    const requestOptions = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    };

    // GET 和 HEAD 请求不能包含 body
    if (method.toUpperCase() !== 'GET' && method.toUpperCase() !== 'HEAD') {
      requestOptions.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, requestOptions);
      const responseData = await response.json();
      if (!response.ok) {
        throw { status: response.status, data: JSON.stringify(responseData) };
      }
      return responseData; // 返回解析后的JSON对象
    } catch (error) {
      // console.error(`There was an error with the request to ${url}:`, error);
      // console.log(error,'error');
      
      // 处理网络错误（如 CORS、网络断开等）
      if (!error.status) {
        // 网络错误，没有 status 属性
        const errorMessage = error.message || '网络请求失败';
        if (!silent && url.indexOf('get_open_cmd_list') == -1) {
          toast.error(`网络错误：${errorMessage}。请检查网络连接或稍后重试。`);
        }
        throw error;
      }
      
      if (this._isAuthError(error)) {
        if (!silent) {
          this._handleAuthExpired();
        }
        throw error;
      }
      
      if (!silent && url.indexOf('get_open_cmd_list') == -1) {
        // 403 错误通常是权限或会员限制，由调用方处理，不在这里显示 toast
        if (error.status !== 403) {
          toast.error(`错误：${JSON.stringify(error)}`)
        }
      }
      throw error; // 将错误抛出，使得调用者能够处理它
    }
  }

  // GET 请求方法（带可选 token）
  // url 应该是完整路径如 /api/v3/skills/list
  async get(url, params = {}) {
    const queryString = Object.entries(params)
      .filter(([_, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    
    const baseUrl = this.apiBase.replace(/\/api$/, '');
    const fullUrl = queryString ? `${baseUrl}${url}?${queryString}` : `${baseUrl}${url}`;
    
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    };
    
    const userData = LocalDataService.load_user_data();
    if (userData?.token) {
      requestOptions.headers['Authorization'] = `Bearer ${userData.token}`;
    }
    
    try {
      const response = await fetch(fullUrl, requestOptions);
      const responseData = await response.json();
      if (!response.ok) {
        throw { status: response.status, data: responseData };
      }
      return responseData;
    } catch (error) {
      if (this._isAuthError(error)) {
        this._handleAuthExpired();
        throw error;
      }
      if (!error.status) {
        const errorMessage = error.message || '网络请求失败';
        toast.error(`网络错误：${errorMessage}`);
      }
      throw error;
    }
  }

  // POST 请求方法（带 token）
  async post(url, data = {}) {
    const baseUrl = this.apiBase.replace(/\/api$/, '');
    const fullUrl = `${baseUrl}${url}`;
    
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(data)
    };
    
    const userData = LocalDataService.load_user_data();
    if (userData?.token) {
      requestOptions.headers['Authorization'] = `Bearer ${userData.token}`;
    }
    
    try {
      const response = await fetch(fullUrl, requestOptions);
      const responseData = await response.json();
      if (!response.ok) {
        throw { status: response.status, data: responseData };
      }
      return responseData;
    } catch (error) {
      if (this._isAuthError(error)) {
        this._handleAuthExpired();
        throw error;
      }
      if (!error.status) {
        const errorMessage = error.message || '网络请求失败';
        toast.error(`网络错误：${errorMessage}`);
      }
      throw error;
    }
  }

  // PUT 请求方法（带 token）
  async put(url, data = {}) {
    const baseUrl = this.apiBase.replace(/\/api$/, '');
    const fullUrl = `${baseUrl}${url}`;
    
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(data)
    };
    
    const userData = LocalDataService.load_user_data();
    if (userData?.token) {
      requestOptions.headers['Authorization'] = `Bearer ${userData.token}`;
    }
    
    try {
      const response = await fetch(fullUrl, requestOptions);
      const responseData = await response.json();
      if (!response.ok) {
        throw { status: response.status, data: responseData };
      }
      return responseData;
    } catch (error) {
      if (this._isAuthError(error)) {
        this._handleAuthExpired();
        throw error;
      }
      if (!error.status) {
        const errorMessage = error.message || '网络请求失败';
        toast.error(`网络错误：${errorMessage}`);
      }
      throw error;
    }
  }

  // DELETE 请求方法（带 token）
  async delete(url) {
    const baseUrl = this.apiBase.replace(/\/api$/, '');
    const fullUrl = `${baseUrl}${url}`;
    
    const requestOptions = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    };
    
    const userData = LocalDataService.load_user_data();
    if (userData?.token) {
      requestOptions.headers['Authorization'] = `Bearer ${userData.token}`;
    }
    
    try {
      const response = await fetch(fullUrl, requestOptions);
      if (response.status === 204) {
        return { success: true };
      }
      const responseData = await response.json();
      if (!response.ok) {
        throw { status: response.status, data: responseData };
      }
      return responseData;
    } catch (error) {
      if (this._isAuthError(error)) {
        this._handleAuthExpired();
        throw error;
      }
      if (!error.status) {
        const errorMessage = error.message || '网络请求失败';
        toast.error(`网络错误：${errorMessage}`);
      }
      throw error;
    }
  }

  // 带 token 的请求方法
  async requestWithToken (url, method, data) {
    const userData = LocalDataService.load_user_data();
    const token = userData?.token;
    
    if (!token) {
      throw new Error('请先登录');
    }

    const requestOptions = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };

    // GET 和 HEAD 请求不能包含 body
    if (method.toUpperCase() !== 'GET' && method.toUpperCase() !== 'HEAD') {
      requestOptions.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, requestOptions);
      
      // 对于 DELETE 请求返回 204 No Content，直接返回成功
      if (response.status === 204) {
        return { success: true };
      }

      const responseData = await response.json();
      if (!response.ok) {
        throw { status: response.status, data: responseData };
      }
      return responseData;
    } catch (error) {
      // 处理网络错误（如 CORS、网络断开等）
      if (!error.status) {
        // 网络错误，没有 status 属性
        const errorMessage = error.message || '网络请求失败';
        toast.error(`网络错误：${errorMessage}。请检查网络连接或稍后重试。`);
        throw error;
      }
      
      if (this._isAuthError(error)) {
        this._handleAuthExpired();
      }
      throw error;
    }
  }

  async getV3ApiKey(forceRefresh = false) {
    const userData = LocalDataService.load_user_data();
    const token = userData?.token;

    if (!token) {
      throw new Error('请先登录');
    }

    const cached = ApiService._v3ApiKeyCache;
    if (!forceRefresh && cached.ownerToken === token && cached.key) {
      return cached.key;
    }

    const response = await this.requestWithToken(
      `${this.apiBase}/v3/apikeys/list`,
      'POST',
      { status: 'active', page: 1, page_size: 20 }
    );
    const apiKey = response?.data?.items?.find(item => item.status === 'active' && item.key)?.key;

    if (!apiKey) {
      throw new Error('未找到可用的 API Key，请前往 API Key 管理页检查');
    }

    ApiService._v3ApiKeyCache = { ownerToken: token, key: apiKey };
    return apiKey;
  }

  _getV3ErrorMessage(responseData, fallback) {
    const detail = responseData?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (typeof responseData?.message === 'string') return responseData.message;
    return fallback;
  }

  async _requestV3(path, method, data, retryOnUnauthorized = true) {
    const apiKey = await this.getV3ApiKey();
    const response = await fetch(`${this.apiBase}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: method === 'GET' || method === 'HEAD' ? undefined : JSON.stringify(data),
    });
    const responseData = await response.json();

    if (!response.ok) {
      if (response.status === 401 && retryOnUnauthorized) {
        ApiService.resetV3ApiKeyCache();
        await this.getV3ApiKey(true);
        return this._requestV3(path, method, data, false);
      }
      const error = new Error(this._getV3ErrorMessage(responseData, 'V3 模型请求失败'));
      error.status = response.status;
      error.data = responseData;
      throw error;
    }

    if (responseData?.code !== undefined && responseData.code !== 200) {
      throw new Error(this._getV3ErrorMessage(responseData, 'V3 模型请求失败'));
    }
    return responseData;
  }

  createV3Task(model, params) {
    return this._requestV3('/v3/tasks/create', 'POST', { model, params });
  }

  quoteV3Task(model, params) {
    return this._requestV3('/v3/tasks/quote', 'POST', { model, params });
  }

  estimateV2Task(appName, inputParams, taskType = null) {
    const userData = LocalDataService.load_user_data();
    if (!userData?.token) {
      return Promise.reject(new Error('请先登录'));
    }
    return this.request(`${this.apiBase}/v2/tasks/estimate-price`, 'POST', {
      token: userData.token,
      user_type: 1,
      app_name: appName,
      input_params: inputParams,
      ...(taskType ? { task_type: taskType } : {}),
    }, { silent: true });
  }

  queryV3Task(taskId) {
    return this._requestV3('/v3/tasks/query', 'POST', { task_id: taskId });
  }

  // 获取角色风格分类（公开）
  get_character_categories () {
    return this.request(`${this.apiBase}/character/categories`, 'GET');
  }

  // 根据风格分类获取角色列表（token 放在 body）
  get_roles_by_category (token, category_id, page = 1, page_size = 12) {
    return this.request(`${this.apiBase}/character/roles/by_category`, 'POST', {
      token,
      category_id,
      page,
      page_size
    });
  }

  // 获取我的角色列表
  get_my_roles (token, page = 1, page_size = 12) {
    return this.request(`${this.apiBase}/character/roles/my_list`, 'POST', {
      token,
      page,
      page_size
    });
  }

  // 创建角色
  create_role (token, name, pen_name, reference_image, reference_video, category_id, is_public) {
    return this.request(`${this.apiBase}/character/role/create`, 'POST', {
      token,
      name,
      pen_name,
      reference_image,
      reference_video,
      category_id,
      is_public
    });
  }

  // 更新角色
  update_role (token, role_id, name, pen_name, category_id, is_public, reference_image, reference_video) {
    return this.request(`${this.apiBase}/character/role/update`, 'POST', {
      token,
      role_id,
      name,
      pen_name,
      category_id,
      is_public,
      reference_image,
      reference_video
    });
  }

  // 检查角色名是否可用
  check_role_name (token, name) {
    return this.request(`${this.apiBase}/character/role/check_name`, 'POST', { token, name, user_type: 1 });
  }

  // 获取角色详情
  get_role_detail (token, role_id) {
    return this.request(`${this.apiBase}/character/role/detail`, 'POST', {
      token,
      role_id
    });
  }

  // 删除角色
  delete_role (token, role_id) {
    return this.request(`${this.apiBase}/character/role/delete`, 'POST', {
      token,
      role_id
    });
  }

  // 提交角色训练
  submit_role_training (token, role_id) {
    return this.request(`${this.apiBase}/character/role/submit_training`, 'POST', {
      token,
      role_id
    });
  }

  // 收藏/取消收藏角色
  favorite_role (token, role_id, action) {
    return this.request(`${this.apiBase}/character/role/favorite`, 'POST', {
      token,
      role_id,
      action
    });
  }

  // 获取收藏的角色列表
  get_favorite_roles (token, page = 1, page_size = 12) {
    return this.request(`${this.apiBase}/character/favorites`, 'POST', {
      token,
      page,
      page_size
    });
  }

  // ==================== 角色案例相关接口 ====================
  
  // 获取角色案例列表
  get_role_case_list (role_id, include_private = 0, include_failed = 0, page = 1, page_size = 20) {
    return this.request(`${this.apiBase}/character/role/case/list`, 'POST', {
      role_id,
      include_private,
      include_failed,
      page,
      page_size
    });
  }

  // 创建角色案例
  create_role_case (token, fal_task_id, custom_title, custom_description, is_public = 1, is_featured = 0) {
    return this.request(`${this.apiBase}/character/role/case/create`, 'POST', {
      token,
      fal_task_id,
      custom_title,
      custom_description,
      is_public,
      is_featured
    });
  }

  // 更新角色案例
  update_role_case (token, case_id, custom_title, custom_description, is_public, is_featured) {
    return this.request(`${this.apiBase}/character/role/case/update`, 'POST', {
      token,
      case_id,
      custom_title,
      custom_description,
      is_public,
      is_featured
    });
  }

  // 删除角色案例
  delete_role_case (token, case_id) {
    return this.request(`${this.apiBase}/character/role/case/delete`, 'POST', {
      token,
      case_id
    });
  }

  // 增加案例查看次数
  increment_case_view (case_id) {
    return this.request(`${this.apiBase}/character/role/case/view`, 'POST', {
      case_id
    });
  }

  // 案例点赞/取消点赞
  toggle_case_like (token, case_id, action) {
    return this.request(`${this.apiBase}/character/role/case/like`, 'POST', {
      token,
      case_id,
      action
    });
  }

  // 获取精选案例
  get_featured_cases (limit = 10) {
    return this.request(`${this.apiBase}/character/role/case/featured`, 'POST', {
      limit
    });
  }

  // 登录方法
  login (phone, password) {
    return this.request(`${this.apiBase}/login`, 'POST', { phone, password });
  }

  //验证码登陆
  login_message (phone, user_type, code_type, code, from_user_id = 0, ref_code = '') {
    return this.request(`${this.apiBase}/login_message`, 'POST', { phone, user_type, code_type, code, from_user_id, ref_code });
  }

  // 注册方法
  register2 (name, phone, password, from_id, user_type, code_type, code, ref_code = '') {
    return this.request(`${this.apiBase}/register2`, 'POST', { name, phone, password, from_id, user_type, code_type, code, ref_code });
  }

  //文本检测
  text_check (token, text) {
    return this.request(`${this.apiBase}/text_check`, 'POST', { token, text });
  }

  //获取小说内容
  get_xiaoshuo_content (token, url) {
    return this.request(`${this.apiBase}/get_xiaoshuo_content`, 'POST', { token, url });
  }


  //获取用户信息
  get_user_info (token) {
    return this.request(`${this.apiBase}/user_info`, 'POST', { token });
  }

  //获取人脸
  get_face_info (image_base64) {
    return this.request(`${this.apiBase}/get_face_info`, 'POST', { image_base64 });
  }



  //获取我的推广用户
  get_my_promote_user (token, user_type, page, page_size) {
    return this.request(`${this.apiBase}/get_my_promote_user`, 'POST', { token, user_type, page, page_size });
  }
  // 创建任务
  create_video_task (token, title, video_url, voice_name, from_lang, to_lang, video_sec, lang_type) {
    return this.request(`${this.apiBase}/create_video_task`, 'POST', { token, title, video_url, voice_name, from_lang, to_lang, video_sec, lang_type });
  }

  // 创建任务
  reset_video_task (token, video_id) {
    return this.request(`${this.apiBase}/reset_video_info`, 'POST', { token, video_id });
  }

  // 获取视频列表
  get_video_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_video_task`, 'POST', { token, begin, count });
  }
  // 获取视频详情
  get_video_info (token, id) {
    return this.request(`${this.apiBase}/get_video_info_by_id`, 'POST', { token, video_id: parseInt(id) });
  }

  //创建文案提取任务
  create_video_to_text_task (token, user_type, mp3_url, video_url, douyin_url) {
    return this.request(`${this.apiBase}/create_video_to_text_task`, 'POST', { token, user_type, mp3_url, video_url, douyin_url });
  }

  get_video_to_text_task_list (token, user_type, begin, count) {
    return this.request(`${this.apiBase}/get_video_to_text_task_list`, 'POST', { token, user_type, begin, count });
  }

  get_audio_task_info (token, task_id) {
    return this.request(`${this.apiBase}/get_audio_task_info`, 'POST', { token, task_id });
  }

  // 
  create_audio_task (token, tts_type, locale, name, role, style, rate, pitch, source_text) {
    return this.request(`${this.apiBase}/create_audio_task`, 'POST', { token, tts_type, locale, name, role, style, rate, pitch, source_text });
  }
  // 获取发布账号列表
  get_video_user_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_video_user_list`, 'POST', { token, begin, count });
  }
  // 获取发布任务的列表
  get_publish_tasks_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_publish_tasks_list`, 'POST', { token, begin, count });
  }
  // 添加登录任务
  add_login_task (token, platform, account_name) {
    return this.request(`${this.apiBase}/add_login_task`, 'POST', { token, platform, account_name });
  }
  // 获取登录二维码和状态
  get_login_task_info (token, task_id) {
    return this.request(`${this.apiBase}/get_login_task_info`, 'POST', { token, task_id });
  }
  // 增加账号托管
  add_video_managed_account (token, account_name, platform, login_info) {
    return this.request(`${this.apiBase}/add_video_managed_account`, 'POST', { token, account_name, platform, login_info });
  }
  // 增加发布任务
  add_publish_task (token, account_ids, title, video_url, cover_image, publish_time) {
    return this.request(`${this.apiBase}/add_publish_task`, 'POST', { token, account_ids, title, video_url, cover_image, publish_time });
  }
  // 修改账号状态
  change_video_account_status (token, status, cid) {
    return this.request(`${this.apiBase}/change_video_account_status`, 'POST', { token, status, cid });
  }
  // 修改发布状态
  change_public_task_status (token, status, cid) {
    return this.request(`${this.apiBase}/change_public_task_status`, 'POST', { token, status, cid });
  }

  // 视频下载
  down_load_video (token, url) {
    return this.request(`${this.apiBase}/down_load_video`, 'POST', { token, url });
  }

  // dell3画图
  create_dall_task (token, prompt, image_width, image_height, quality, style) {
    return this.request(`${this.apiBase}/create_dall_task`, 'POST', { token, prompt, image_width, image_height, quality, style });
  }

  // 修改图片状态
  change_dall_task_by_id (token, task_id, status) {
    return this.request(`${this.apiBase}/change_dall_task_by_id`, 'POST', { token, task_id, status });
  }

  // 图片列表
  get_dall_tasks_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_dall_tasks_list`, 'POST', { token, begin, count });
  }

  // 单图
  get_dall_task_by_id (token, task_id) {
    return this.request(`${this.apiBase}/get_dall_task_by_id`, 'POST', { token, task_id });
  }

  // 获取支付列表
  get_pay_info_list (token, shop_type, options = {}) {
    return this.request(`${this.apiBase}/get_pay_info_list`, 'POST', { token, shop_type }, options);
  }

  // 创建微信订单
  create_wx_order_info (token, shop_id, options = {}) {
    return this.request(`${this.apiBase}/create_wx_order_info`, 'POST', { token, shop_id }, options);
  }


  // 创建微信订单
  create_wx_order_info_app (token, shop_id , app_id) {
    return this.request(`${this.apiBase}/create_wx_order_info_app`, 'POST', { token, shop_id , app_id});
  }

  // 创建支付宝订单
  create_alipay_order_info (token, shop_id, options = {}) {
    return this.request(`${this.apiBase}/create_alipay_order_info`, 'POST', { token, shop_id }, options);
  }

  // 获取订单信息
  get_order_info (token, out_trade_no, options = {}) {
    return this.request(`${this.apiBase}/get_order_info`, 'POST', { token, out_trade_no }, options);
  }


  // 注销账号
  unregister (token) {
    return this.request(`${this.apiBase}/unregister`, 'POST', { token });
  }


  // 创建换脸任务
  create_face_task (data) {
    return this.request(`${this.apiBase}/create_face_task`, 'POST', data);
  }

  get_face_task_list (token, begin, count, task_type) {
    return this.request(`${this.apiBase}/get_face_task_list`, 'POST', { token, begin, count, task_type });
  }

  //获取风格
  get_style_list (token, begin = 0, end = 40) {
    return this.request(`${this.apiBase}/get_style_list`, 'POST', { token, begin, end });
  }

  //获取素材
  get_face_materials (token, begin, count, task_type) {
    return this.request(`${this.apiBase}/get_face_materials`, 'POST', { token, begin, count, task_type });
  }

  // 创建换脸任务
  create_video_gen_task (data) {
    return this.request(`${this.apiBase}/create_video_gen_task`, 'POST', data);
  }

  //更新字段
  book_detail_update (data) {
    return this.request(`${this.apiBase}/book_detail_update`, 'POST', data);
  }

  //生成文本内容
  book_generate_script (token, title, language, paragraph_number) {
    return this.request(`${this.apiBase}/book_generate_script`, 'POST', { token, title, language, paragraph_number });
  }

  //生成文本关键词
  book_generate_terms (token, title, content, amount) {
    return this.request(`${this.apiBase}/book_generate_terms`, 'POST', { token, title, content, amount });
  }

  //提交生成视频的任务
  text_gen_video (data) {
    return this.request(`${this.apiBase}/text_gen_video`, 'POST', data);
  }
  //获取视频列表
  get_text_gen_video_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_text_gen_video_list`, 'POST', { token, begin, count });
  }

  get_video_gen_task_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_video_gen_task_list`, 'POST', { token, begin, count });
  }

  //获取小说制作列表
  get_book_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_book_list`, 'POST', { token, begin, count });
  }
  ///api/book_create
  //小说创建
  book_create (data) {
    return this.request(`${this.apiBase}/book_create`, 'POST', data);
  }

  //删除小说
  delete_book (token, book_id) {
    return this.request(`${this.apiBase}/delete_book`, 'POST', { token, book_id });
  }

  //获取小说制作详情
  get_book_detail_by_id (token, book_id, begin, count) {
    return this.request(`${this.apiBase}/get_book_detail_by_id`, 'POST', { token, book_id, begin, count });
  }

  get_promotion_income_info (token) {
    return this.request(`${this.apiBase}/get_promotion_income_info`, 'POST', { token });
  }

  get_invite_user_list (token, name, type_invite, begin, count) {
    return this.request(`${this.apiBase}/get_invite_user_list`, 'POST', { token, name, type_invite, begin, count });
  }

  referral_me_status (token) {
    return this.request(`${this.apiBase}/referral/me/status`, 'POST', { token });
  }

  referral_me_dashboard (token) {
    return this.request(`${this.apiBase}/referral/me/dashboard`, 'POST', { token });
  }

  referral_me_invite_list (token, view_type, page, page_size, ref_code = '') {
    return this.request(`${this.apiBase}/referral/me/invite_list`, 'POST', { token, view_type, page, page_size, ref_code });
  }

  referral_me_code_list (token) {
    return this.request(`${this.apiBase}/referral/me/code/list`, 'POST', { token });
  }

  referral_me_code_create (token, remark = '') {
    return this.request(`${this.apiBase}/referral/me/code/create`, 'POST', { token, remark });
  }

  referral_me_code_update_remark (token, code_id, remark = '') {
    return this.request(`${this.apiBase}/referral/me/code/update_remark`, 'POST', { token, code_id, remark });
  }

  referral_visit_report (ref_code, visitor_id, event_id = '', page_path = '') {
    return this.request(`${this.apiBase}/referral/visit/report`, 'POST', {
      ref_code,
      visitor_id,
      event_id,
      page_path
    }, { silent: true });
  }

  referral_me_code_visit_trend (token, ref_code, days = 30) {
    return this.request(`${this.apiBase}/referral/me/code/visit_trend`, 'POST', { token, ref_code, days });
  }

  apply_for_extract (token, alipay_account, name, money) {
    return this.request(`${this.apiBase}/apply_for_extract`, 'POST', { token, alipay_account, name, money });
  }
  //获取镜像列表
  get_docker_images_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_docker_images_list`, 'POST', { token, begin, count });
  }

  //获取机器地区
  get_compute_region (token) {
    return this.request(`${this.apiBase}/get_compute_region`, 'POST', { token });
  }

  //获取机器地区库存
  get_compute_region_stock (token, region_sign) {
    return this.request(`${this.apiBase}/get_compute_region_stock`, 'POST', { token, region_sign });
  }

  //创建部署
  create_compute_deployment (token, region_sign, gpu_name_set, image_uuid) {
    return this.request(`${this.apiBase}/create_compute_deployment`, 'POST', { token, region_sign, gpu_name_set, image_uuid });
  }

  //获取容器
  get_compute_combined (token) {
    return this.request(`${this.apiBase}/get_compute_combined`, 'POST', { token });
  }

  //关闭容器
  close_compute_combined (token, deployment_uuid) {
    return this.request(`${this.apiBase}/close_compute_combined`, 'POST', { token, deployment_uuid });
  }

  //获取余额
  get_user_money (token, options = {}) {
    return this.request(`${this.apiBase}/get_user_money`, 'POST', { token }, options);
  }

  //获取用户金额明细
  get_user_money_detail (token, begin, count) {
    return this.request(`${this.apiBase}/get_user_money_detail`, 'POST', { token, begin, count });
  }

  //获取洗视频列表
  get_change_video_task_list (token, begin, count) {
    return this.request(`${this.apiBase}/get_change_video_task_list`, 'POST', { token, begin, count });
  }
  //获取bgm
  get_audio_bgm_url (token) {
    return this.request(`${this.apiBase}/get_audio_bgm_url`, 'POST', { token });
  }

  //添加洗视频任务
  create_change_video_task (token, title, video_url, tts_name, tts_speed, sd_model, width, height, denoising_strength, check_type, img_pos, prompt, negative_prompt, bgm_url, is_change_text) {
    return this.request(`${this.apiBase}/create_change_video_task`, 'POST', { token, title, video_url, tts_name, tts_speed, sd_model, width, height, denoising_strength, check_type, img_pos, prompt, negative_prompt, bgm_url, is_change_text });
  }
  //获取洗视频详情
  get_change_video_task_by_id (token, video_id, begin, count) {
    return this.request(`${this.apiBase}/get_change_video_task_by_id`, 'POST', { token, video_id, begin, count });
  }

  //局部更新
  update_video_detail (token, video_id, _updates) {
    return this.request(`${this.apiBase}/update_video_detail`, 'POST', { token, video_id, updates: { ..._updates } });
  }

  //算力兑换码
  consume_activation_code (token, activation_code_info) {
    return this.request(`${this.apiBase}/consume_activation_code`, 'POST', { token, activation_code_info, user_type: 1 });
  }

  //获取验证码
  get_message_code (phone, user_type, code_type) {
    return this.request(`${this.apiBase}/get_message_code`, 'POST', { phone, user_type, code_type });
  }

  //创建合集
  create_batch_collection (token, title, input_list) {
    return this.request(`${this.apiBase}/create_batch_collection`, 'POST',
      {
        "token": token,
        "user_type": 1,
        "title": title,
        "input_list": input_list
      }
    );
  }

  get_batch_list (token, user_type, page, size) {
    return this.request(`${this.apiBase}/user_batch_collections`, 'POST', { token, user_type, page, size });
  }

  get_batch_collection (collection_id, page = 1, size = 200) {
    return this.request(`${this.apiBase}/get_batch_collection`, 'POST', { collection_id, page, size });
  }


  get_user_coze_token (token, user_type) {
    return this.request(`${this.apiBase}/get_user_coze_token`, 'POST', { token, user_type });
  }

  update_user_token (token, user_type, coze_token) {
    return this.request(`${this.apiBase}/update_user_token`, 'POST', { token, user_type, coze_token });

  }

  get_user_bot_list (token, user_type, page = 1, size = 20) {
    return this.request(`${this.apiBase}/get_user_bot_list`, 'POST', { token, user_type, page, size });
  }

  get_user_bot_by_id (token, user_type, bot_id) {
    return this.request(`${this.apiBase}/get_user_bot_by_id`, 'POST', { token, user_type, bot_id });
  }

  add_user_bot (token, user_type, title, bot_url) {
    return this.request(`${this.apiBase}/add_user_bot`, 'POST', { token, user_type, title, bot_url });
  }


  del_user_bot (token, user_type, bot_id) {
    return this.request(`${this.apiBase}/del_user_bot`, 'POST', { token, user_type, bot_id });
  }

  update_user_bot (token, user_type, title, bot_url, bot_id) {
    return this.request(`${this.apiBase}/update_user_bot`, 'POST', { token, user_type, title, bot_url, bot_id });
  }

  open_bot_status (token, user_type, bot_id, status) {
    return this.request(`${this.apiBase}/open_bot_status`, 'POST', { token, user_type, bot_id, status });

  }

  get_bot_cmds (token, user_type, bot_id) {
    return this.request(`${this.apiBase}/get_bot_cmds`, 'POST', { token, user_type, bot_id });
  }

  del_bot_cmd (token, user_type, cmd_id) {
    return this.request(`${this.apiBase}/del_bot_cmd`, 'POST', { token, user_type, cmd_id });
  }

  add_bot_cmd (token, user_type, title, cmd, bot_id) {
    return this.request(`${this.apiBase}/add_bot_cmd`, 'POST', { token, user_type, title, cmd, bot_id });
  }

  get_open_cmd_list (token, user_type) {
    return this.request(`${this.apiBase}/get_open_cmd_list`, 'POST', { token, user_type });
  }
  //获取插件列表
  getPluginList () {
    return this.request(`${this.apiBase}/get_plugin_list`, 'POST', {});
  }

  get_api_token (token, user_type) {
    return this.request(`${this.apiBase}/get_api_token`, 'POST', { token, user_type });
  }

  //获取coze模板列表
  get_draft_template (page, page_size) {
    return this.request(`${this.apiBase}/get_draft_template`, 'POST', { page, page_size });
  }

  //获取公众号登录链接
  get_qrcode (client_source = '') {
    const params = new URLSearchParams();
    if (client_source) params.set('client_source', client_source);
    const query = params.toString();
    const url = query ? `${this.apiBase}/get_qrcode?${query}` : `${this.apiBase}/get_qrcode`;
    return this.request(url, 'GET');
  }

  //查询扫码状态
  check_qrcode_status (scene_id, from_user_id, ref_code = '') {
    return this.request(`${this.apiBase}/check_qrcode_status`, 'POST', { scene_id, from_user_id: from_user_id, ref_code });
  }
  //获取剪映草稿记录
  get_jydraft_record (token, user_type, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/get_jydraft_record`, 'POST', { token, user_type, page, page_size });
  }

  //更新api_token
  reset_api_token (token) {
    return this.request(`${this.apiBase}/reset_api_token`, 'POST', { token });
  }

  //获取签约信息
  get_contract_info (token) {
    return this.request(`${this.apiBase}/get_contract_info`, 'POST', { token, user_type: 1, });
  }

  //提交签约信息
  async submit_contract (formData) {
    try {
      const response = await fetch(`${this.apiBase}/submit_contract`, {
        method: 'POST',
        body: formData // 已经是FormData格式，不需要JSON.stringify
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('签约提交失败:', error);
      throw error;
    }
  }

  //提交提现记录
  async submit_withdraw (token, amount) {
    try {
      const response = await fetch(`${this.apiBase}/submit_withdraw_record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          token,
          user_type: 1,
          amount
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('提现申请失败:', error);
      throw error;
    }
  }

  //获取提现记录列表
  get_withdraw_record_list (token, page, page_size) {
    return this.request(`${this.apiBase}/get_withdraw_record_list`, 'POST', { token, page, page_size, user_type: 1 });
  }

  //创建数字人
  create_digital_human (token, digital_human_name, video_url) {
    return this.request(`${this.apiBase}/create_digital_human`, 'POST', { token, digital_human_name, video_url, user_type: 1 });
  }

  //获取数字人列表
  get_digital_human_list (token, page, page_size) {
    return this.request(`${this.apiBase}/get_digital_human_list`, 'POST', { token, page, page_size, user_type: 1 });
  }

  //删除数字人
  delete_digital_human (token, id) {
    return this.request(`${this.apiBase}/delete_digital_human`, 'POST', { token, id, user_type: 1 });
  }

  //获取上传视频token
  get_sts_token () {
    return this.request(`${this.apiBase}/get_sts_token`, 'POST', {});
  }
  //创建语音库
  create_voice_library (token, voice_name, audio_url, audio_content, sex, language) {
    return this.request(`${this.apiBase}/create_voice_library`, 'POST', { token, voice_name, audio_url, audio_content, sex, language, user_type: 1 });
  }

  //获取语音库列表
  get_voice_library_list (token, page, page_size) {
    return this.request(`${this.apiBase}/get_voice_library_list`, 'POST', { token, page, page_size, user_type: 1 });
  }
  //删除语音库
  delete_voice_library (token, id) {
    return this.request(`${this.apiBase}/delete_voice_library`, 'POST', { token, id, user_type: 1 });
  }

  //创建数字人视频
  create_digital_human_video (token, digital_id, tts_text, audio_url, voice_id, mode) {
    return this.request(`${this.apiBase}/create_digital_human_video`, 'POST', { token, digital_id, tts_text, audio_url, voice_id, mode, user_type: 1 });
  }

  //删除对口型任务
  delete_lip_sync_task (token, id) {
    return this.request(`${this.apiBase}/delete_lip_sync_task`, 'POST', { token, id, user_type: 1 });
  }

  // 获取唇形同步任务列表
  get_lip_sync_tasks (token, page, page_size) {
    return this.request(`${this.apiBase}/get_lip_sync_tasks`, 'POST', { token, page, page_size, user_type: 1 });
  }

  // 创建语音合成任务
  create_voice_synthesis_task (token, from_voice_id, tts_text, mode) {
    return this.request(`${this.apiBase}/create_voice_synthesis_task`, 'POST', { token, from_voice_id, tts_text, mode, user_type: 1 });
  }

  // 获取语音合成任务列表
  get_voice_synthesis_tasks (token, page, page_size) {
    return this.request(`${this.apiBase}/get_voice_synthesis_tasks`, 'POST', { token, page, page_size, user_type: 1 });
  }

  // 删除语音合成任务
  delete_voice_synthesis_task (token, id) {
    return this.request(`${this.apiBase}/delete_voice_synthesis_task`, 'POST', { token, id, user_type: 1 });
  }

  // 获取我的空间
  get_my_space (token) {
    return this.request(`${this.apiBase}/get_my_space`, 'POST', { token, user_type: 1 });
  }

  // 添加空间
  add_space (token, url) {
    return this.request(`${this.apiBase}/add_space`, 'POST', { token, url, user_type: 1 });
  }

  // 删除空间
  delete_space (token, s_id) {
    return this.request(`${this.apiBase}/delete_space`, 'POST', { token, s_id, user_type: 1 });
  }

  // 获取智能体列表
  get_bot_list (token, page = 1, size = 10, is_offical = null, category = null) {
    const params = { token, page, size, user_type: 1 };
    if (is_offical !== null) {
      params.is_offical = is_offical;
    }
    if (category !== null) {
      params.category = category;
    }
    return this.request(`${this.apiBase}/get_bot_list`, 'POST', params);
  }

  // 获取复制任务列表
  get_migration_tasks (token, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/get_migration_tasks`, 'POST', { token, page, page_size, user_type: 1 });
  }

  // 迁移任务
  add_migration_tasks (token, bot_ids, space_id) {
    return this.request(`${this.apiBase}/add_migration_tasks`, 'POST', { token, bot_ids, space_id, user_type: 1 });
  }

  // 取消迁移任务
  cancel_migration_task (token, task_id) {
    return this.request(`${this.apiBase}/cancel_migration_task`, 'POST', { token, task_id, user_type: 1 });
  }

  // 获取智能体详情
  get_bot_info (token, bot_id) {
    return this.request(`${this.apiBase}/get_bot_info`, 'POST', { token, bot_id, user_type: 1 });
  }

  // 更新用户信息
  update_user_info (token, name, head_img) {
    return this.request(`${this.apiBase}/update_user_info`, 'POST', { token, user_type: 1, name, head_img });
  }

  // 获取公开智能体案例列表（无需认证）
  get_public_bot_case_list (bot_id, page, page_size) {
    return this.request(`${this.apiBase}/get_bot_case_list`, 'POST', { bot_id, page, page_size, user_type: 1 });
  }

  // 提交优秀案例模板
  submit_draft_template (token, title, img_url, video_url, url) {
    return this.request(`${this.apiBase}/submit_draft_template`, 'POST', { token, title, img_url, video_url, url, user_type: 1 });
  }

  // 获取Excel表格数据
  get_excel_data (tableId, columnName = '', page = 1, pageSize = 100) {
    return this.request(`${this.apiBase}/excel/data/${tableId}`, 'POST', { column_name: columnName, page, page_size: pageSize });
  }

  // 获取等待狗记录列表
  get_cozetask_list (token, page = 1, pageSize = 10, user_type = 1) {
    return this.request(`${this.apiBase}/cozetask/list`, 'POST', { token, page, pageSize, user_type });
  }

  // 获取单个等待狗任务详情
  get_cozetask_info (token, task_id) {
    return this.request(`${this.apiBase}/get_cozetask_info`, 'POST', { token, task_id });
  }

  // 保存 OSS 上传记录
  saveOssUploadRecord (token, file_url, file_type, file_size) {
    return this.request(`${this.apiBase}/user_oss_upload`, 'POST', {
      token,
      user_type: 1,
      file_url,
      file_type,
      file_size
    });
  }

  // 获取文件列表
  getFileList (token, page = 1, page_size = 10, user_type = 1) {
    return this.request(`${this.apiBase}/get_file_list`, 'POST', { token, page, page_size, user_type });
  }

  // 每日签到
  check_in (token, user_type) {
    return this.request(`${this.apiBase}/check_in`, 'POST', { token, user_type });
  }

  // 获取用户签到历史记录
  get_check_in_history (token, user_type, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/get_check_in_history`, 'POST', { token, user_type, page, page_size });
  }

  // 获取官网活动列表
  get_activity_banner_list (activity_type) {
    return this.request(`${this.apiBase}/get_activity_banner_list`, 'POST', { activity_type });
  }
  // 分类获取课程列表
  courses (page, page_size) {
    return this.request(`${this.apiBase}/courses`, 'POST', { page, page_size });
  }
  // 获取课程详情（章节）
  get_chapters (token_or_course_id, course_id) {
    // 如果第二个参数存在，说明第一个参数是token
    if (course_id !== undefined) {
      // 创作者模式：需要token
      return this.request(`${this.apiBase}/courses/chapters`, 'POST', { token: token_or_course_id, course_id, user_type: 1 });
    } else {
      // 公开模式：不需要token，第一个参数就是course_id
      return this.request(`${this.apiBase}/courses/chapters`, 'POST', { course_id: token_or_course_id, user_type: 1 });
    }
  }

  // 课程管理相关API
  // 创建课程
  create_course (token, title, subtitle, description, cover_image, category_id, price, tags) {
    return this.request(`${this.apiBase}/courses/create`, 'POST', { 
      token, title, subtitle, description, cover_image, category_id, price, tags 
    });
  }

  // 更新课程
  update_course (token, course_id, title, subtitle, description, cover_image, category_id, price, tags, is_completed, status) {
    return this.request(`${this.apiBase}/courses/update`, 'POST', { 
      token, course_id, title, subtitle, description, cover_image, category_id, price, tags, is_completed, status 
    });
  }

  // 删除课程
  delete_course (token, course_id) {
    return this.request(`${this.apiBase}/courses/delete`, 'POST', { token, course_id });
  }

  // 创建章节
  create_chapter (token, course_id, title, description, video_url, duration, cover_image, is_free) {
    return this.request(`${this.apiBase}/courses/chapters/create`, 'POST', { 
      token, course_id, title, description, video_url, duration, cover_image, is_free 
    });
  }

  // 更新章节
  update_chapter (token, chapter_id, title, description, video_url, duration, cover_image, is_free, status) {
    return this.request(`${this.apiBase}/courses/chapters/update`, 'POST', { 
      token, chapter_id, title, description, video_url, duration, cover_image, is_free, status 
    });
  }

  // 删除章节
  delete_chapter (token, chapter_id) {
    return this.request(`${this.apiBase}/courses/chapters/delete`, 'POST', { token, chapter_id });
  }

  // 获取用户创建的课程列表
  get_my_courses (token, page = 1, page_size = 10, status = null, title = null) {
    const requestData = {
      token,
      page,
      page_size
    };
    
    // 添加可选的筛选参数
    if (status !== null) {
      requestData.status = status;
    }
    if (title !== null) {
      requestData.title = title;
    }
    
    return this.request(`${this.apiBase}/courses/my_courses`, 'POST', requestData);
  }

  // 章节点播接口
  play_chapter (token, course_id, chapter_id) {
    return this.request(`${this.apiBase}/courses/chapters/play`, 'POST', { token, course_id, chapter_id, user_type: 1 });
  }

  // 使用余额购买课程
  purchase_course (token, course_id) {
    return this.request(`${this.apiBase}/courses/purchase`, 'POST', { token, course_id, user_type: 1 });
  }

  // 提交课程审核
  async submit_course_review (token, course_id) {
    try {
      const response = await fetch(`${this.apiBase}/courses/submit_review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          course_id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('提交课程审核失败:', error);
      throw error;
    }
  }

  // 提交章节审核
  async submit_chapter_review (token, chapter_id) {
    try {
      const response = await fetch(`${this.apiBase}/courses/chapters/submit_review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          chapter_id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('提交章节审核失败:', error);
      throw error;
    }
  }

  // 图片上传相关接口
  // 获取coze token
  get_coze_token () {
    return this.request(`${this.apiBase}/fal/tasks/coze_token`, 'GET', {});
  }

  // 上传图片到coze（这个方法需要特殊处理，因为是multipart/form-data）
  async upload_image_to_coze (file, uploadToken) {
    const formData = new FormData();
    formData.append('file', file);

    const requestOptions = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${uploadToken}`,
        'accept': 'application/json'
        // 注意：不要设置 Content-Type，让浏览器自动设置 multipart/form-data 边界
      },
      body: formData
    };

    console.log('准备上传图片到coze:', {
      url: 'https://api.coze.cn/v1/files/upload',
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      tokenPrefix: uploadToken.substring(0, 20) + '...'
    });

    try {
      const response = await fetch('https://api.coze.cn/v1/files/upload', requestOptions);
      
      console.log('Coze上传响应状态:', response.status, response.statusText);
      console.log('Coze上传响应头:', Object.fromEntries(response.headers.entries()));
      
      const responseData = await response.json();
      console.log('Coze上传响应数据:', responseData);
      
      if (!response.ok) {
        const errorInfo = {
          status: response.status,
          statusText: response.statusText,
          data: responseData,
          headers: Object.fromEntries(response.headers.entries())
        };
        console.error('Coze上传失败详情:', errorInfo);
        throw new Error(`HTTP ${response.status}: ${responseData.msg || responseData.message || response.statusText}`);
      }
      
      return responseData;
    } catch (error) {
      console.error('上传图片到coze失败:', error);
      
      // 检查是否是网络错误
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('网络连接失败，可能是CORS问题或网络不可达');
      }
      
      // 检查是否是CORS错误
      if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
        throw new Error('跨域请求被阻止，请检查CORS配置');
      }
      
      throw error;
    }
  }

  // 通过file_id获取图片永久地址
  async get_image_url_by_file_id (fileId, accessToken) {
    const requestBody = {
      parameters: {
        input: JSON.stringify({ file_id: fileId })
      },
      workflow_id: "7527555709244801087"  // 使用接口文档中的正确workflow_id
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    };

    console.log('准备获取图片永久地址:', {
      url: 'https://api.coze.cn/v1/workflow/run',
      fileId: fileId,
      workflowId: "7527555709244801087",
      tokenPrefix: accessToken.substring(0, 20) + '...',
      requestBody: requestBody
    });

    try {
      const response = await fetch('https://api.coze.cn/v1/workflow/run', requestOptions);
      
      console.log('Workflow响应状态:', response.status, response.statusText);
      console.log('Workflow响应头:', Object.fromEntries(response.headers.entries()));
      
      const responseData = await response.json();
      console.log('Workflow响应数据:', responseData);
      
      if (!response.ok) {
        const errorInfo = {
          status: response.status,
          statusText: response.statusText,
          data: responseData,
          headers: Object.fromEntries(response.headers.entries())
        };
        console.error('Workflow执行失败详情:', errorInfo);
        throw new Error(`HTTP ${response.status}: ${responseData.msg || responseData.message || response.statusText}`);
      }
      
      return responseData;
    } catch (error) {
      console.error('获取图片永久地址失败:', error);
      
      // 检查是否是网络错误
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('网络连接失败，无法访问Coze Workflow API');
      }
      
      // 检查是否是CORS错误
      if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
        throw new Error('跨域请求被阻止，无法访问Coze Workflow API');
      }
      
      throw error;
    }
  }

  // 完整的图片上传流程（根据图片上传逻辑文档修复）
  async upload_image_complete (file) {
    try {
      console.log('开始图片上传流程，文件:', file.name);
      
      // 1. 获取coze token
      console.log('步骤1: 获取coze token');
      const tokenResponse = await this.get_coze_token();
      console.log('Token响应:', tokenResponse);
      
      if (!tokenResponse || !tokenResponse.upload_key || !tokenResponse.data) {
        throw new Error('获取coze token失败');
      }

      const uploadToken = tokenResponse.upload_key;  // 用于上传的token
      const accessToken = tokenResponse.data;        // 用于获取永久地址的token

      console.log('上传Token:', uploadToken);
      console.log('访问Token:', accessToken);

      // 2. 上传图片到coze
      console.log('步骤2: 上传图片到coze');
      const uploadResponse = await this.upload_image_to_coze(file, uploadToken);
      console.log('上传响应:', uploadResponse);
      
      if (!uploadResponse || !uploadResponse.data || !uploadResponse.data.id) {
        throw new Error('上传图片失败');
      }

      const fileId = uploadResponse.data.id;
      console.log('获得文件ID:', fileId);

      // 3. 获取图片永久地址
      console.log('步骤3: 获取图片永久地址');
      const urlResponse = await this.get_image_url_by_file_id(fileId, accessToken);
      console.log('永久地址响应:', urlResponse);
      
      // 解析永久地址
      let permanentUrl = '';
      if (urlResponse && urlResponse.data) {
        try {
          // 根据接口文档，data字段是JSON字符串，需要解析
          let parsedData;
          if (typeof urlResponse.data === 'string') {
            parsedData = JSON.parse(urlResponse.data);
          } else {
            parsedData = urlResponse.data;
          }
          
          // 从解析后的数据中获取output字段
          if (parsedData && parsedData.output) {
            permanentUrl = parsedData.output;
          }
          
          console.log('解析后的数据:', parsedData);
          console.log('提取的永久地址:', permanentUrl);
        } catch (parseError) {
          console.warn('解析data失败:', parseError);
          console.warn('原始data:', urlResponse.data);
          
          // 如果解析失败，尝试直接使用data字段
          if (typeof urlResponse.data === 'string' && urlResponse.data.startsWith('http')) {
            permanentUrl = urlResponse.data;
          }
        }
      }
      
      console.log('解析得到的永久地址:', permanentUrl);
      
      return {
        success: true,
        file_id: fileId,
        permanent_url: permanentUrl,
        url_response: urlResponse,
        upload_info: uploadResponse.data
      };
    } catch (error) {
      console.error('完整图片上传流程失败:', error);
      throw error;
    }
  }
  // 获取官网任务列表
  get_tasks (token) {
    return this.request(`${this.apiBase}/tasks/list`, 'POST', { token, user_type: 1 });
  }
  // 获取官网任务详情
  get_task_reward (token, task_id) {
    return this.request(`${this.apiBase}/task/reward`, 'POST', { token, task_id, user_type: 1, });
  }

  // 获取工作流信息
  get_workflow_info (token, workflow_id) {
    return this.request(`${this.apiBase}/get_workflow_info`, 'POST', { token, workflow_id, user_type: 1 });
  }


  // 执行工作流
  async execute_workflow (token, workflow_id, parameters) {
    return this.request(`${this.apiBase}/add_workflow_execute_record`, 'POST', { token, workflow_id, parameters, user_type: 1 });
  }

  // 获取工作流执行记录
  get_workflow_execute_records (token, params) {
    return this.request(`${this.apiBase}/get_workflow_execute_records`, 'POST', {
      token,
      workflow_id: params.workflow_id,
      page: params.page,
      size: params.size,
      user_type: params.user_type || 1
    });
  }

  // 获取即梦列表
  get_jimeng_task_list (token, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/jimeng/task/user_list`, 'POST', {
      token,
      user_type: 1,
      page,
      page_size
    });
  }

  // 获取海螺任务列表
  get_hailuo_task_list (token, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/task_list`, 'POST', {
      token,
      user_type: 1,
      page,
      page_size
    });
  }

  // 获取豆包任务列表
  get_doubao_task_list (token, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/doubao_task_list`, 'POST', {
      token,
      user_type: 1,
      page,
      page_size
    });
  }

  // 获取FAL任务列表
  get_fal_task_list (token, page = 1, page_size = 10, app_name = '', status = '', options = {}) {
    return this.request(`${this.apiBase}/fal/tasks/list`, 'POST', {
      token,
      user_type: 1,
      page,
      page_size,
      app_name,
      status
    }, options);
  }

  // 获取用户余额信息
  get_user_money_info (token) {
    return this.request(`${this.apiBase}/get_user_money`, 'POST', {
      token,
      user_type: 1
    });
  }

  // 扣除用户余额
  deduct_user_money (token, money, title) {
    return this.request(`${this.apiBase}/deduct_user_money`, 'POST', {
      token,
      money,
      title,
      user_type: 1
    });
  }

  // 创建即梦任务（支持图生视频和数字人）
  async create_jimeng_img2video_task (token, img_url, prompt, model, duration = 5, audio_url = '', task_type = 1) {
    try {
      const requestData = {
        task_type: task_type, // 1: 图生视频, 2: 数字人
        first_image: img_url
      };

      if (task_type === 1) {
        // 图生视频模式
        requestData.prompt = prompt;
        requestData.model = model;
        requestData.duration = duration;
        if (audio_url) {
          requestData.mp3_url = audio_url;
        }
      } else if (task_type === 2) {
        // 数字人模式
        requestData.mp3_url = audio_url;
        // 数字人模式需要计算费用
        if (audio_url) {
          // 这里需要根据音频时长计算费用，暂时使用默认值
          requestData.money = 40; // 将在调用处计算具体费用
        }
      }

      const response = await fetch(ENDPOINTS.JIMENG_TASK_CREATE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('创建即梦任务失败:', error);
      throw error;
    }
  }


  // 获取抖音授权列表
  get_douyin_auth_list (token, page, page_size, promotion_name) {
    return this.request(`${this.apiBase}/get_douyin_auth_list`, 'POST', {
      token,
      user_type: 1,
      promotion_name,
      page,
      page_size,
    });
  }

  // 添加抖音授权账号
  add_douyin_auth (token, douyin_nickname, douyin_uid, homepage_screenshot_url, douyin_homepage_url, jianying_uid, promotion_name) {
    return this.request(`${this.apiBase}/add_douyin_auth`, 'POST', {
      token,
      user_type: 1,
      douyin_nickname,
      douyin_uid,
      homepage_screenshot_url,
      douyin_homepage_url,
      jianying_uid,
      promotion_name
    });
  }

  // 删除抖音授权账号
  delete_douyin_auth (token, auth_id) {
    return this.request(`${this.apiBase}/delete_douyin_auth`, 'POST', {
      token,
      auth_id,
      user_type: 1
    });
  }

  // 获取回填记录列表
  get_backfill_list (token, page, page_size, auth_id) {
    return this.request(`${this.apiBase}/get_backfill_list`, 'POST', {
      token,
      page,
      page_size,
      user_type: 1,
      auth_id
    });
  }

  // 添加回填记录
  add_backfill (token, auth_id, backfill_url, video_gid) {
    return this.request(`${this.apiBase}/add_backfill`, 'POST', {
      token,
      user_type: 1,
      auth_id,
      backfill_url,
      video_gid,
    });
  }

  // 删除回填记录
  delete_backfill (token, backfill_id) {
    return this.request(`${this.apiBase}/delete_backfill`, 'POST', {
      token,
      backfill_id,
      user_type: 1
    });
  }

  // 获取项目收入
  get_project_income (token, promotion_name, page, page_size) {
    return this.request(`${this.apiBase}/get_project_income`, 'POST', {
      token,
      promotion_name,
      user_type: 1,
      page,
      page_size
    });
  }

  // 创建故事
  create_story (token, story_name, story_description, aspect_ratio, style, story_type, user_type) {
    return this.request(`${this.apiBase}/story/create`, 'POST', {
      token,
      story_name,
      story_description,
      aspect_ratio,
      style,
      story_type,
      user_type
    });
  }

  // 获取故事列表
  get_story_list (token, page = 1, page_size = 10, user_type = 1) {
    return this.request(`${this.apiBase}/story/list`, 'POST', {
      token,
      page,
      page_size,
      user_type
    });
  }

  // 获取故事分镜列表
  get_storyboard_list (token, story_uuid, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/list`, 'POST', {
      story_uuid,
      token,
      user_type
    });
  }

  // 批量创建分镜
  batch_create_storyboards (token, story_uuid, storyboards, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/batch_create`, 'POST', {
      story_uuid,
      storyboards,
      token,
      user_type
    });
  }

  // 更新分镜信息
  update_storyboard (token, storyboard_uuid, storyboard_data, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/update`, 'POST', {
      token,
      user_type,
      storyboard_uuid,
      ...storyboard_data
    });
  }

  // 插入分镜
  insert_storyboard (token, story_uuid, insert_after, storyboard_data, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/insert`, 'POST', {
      token,
      user_type,
      story_uuid,
      insert_after,
      ...storyboard_data
    });
  }

  // 删除分镜
  delete_storyboard (token, storyboard_uuid, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/delete`, 'POST', {
      token,
      storyboard_uuid,
      user_type
    });
  }

  // 获取故事角色列表
  get_story_characters (token, story_uuid, user_type = 1) {
    return this.request(`${this.apiBase}/story/characters`, 'POST', {
      token,
      story_uuid,
      user_type
    });
  }

  // 创建故事角色
  create_story_character (token, story_uuid, character_data, user_type = 1) {
    return this.request(`${this.apiBase}/character/create`, 'POST', {
      story_uuid,
      character_name: character_data.character_name || '',
      character_description: character_data.character_description || '',
      is_public: character_data.is_public || false,
      gender: character_data.gender || '未知',
      profession: character_data.profession || '',
      style: character_data.style || '',
      clothing: character_data.clothing || '',
      appearance: character_data.appearance || '',
      personality: character_data.personality || '',
      background: character_data.background || '',
      avatar_image: character_data.avatar_image || '',
      full_image: character_data.full_image || '',
      age_range: character_data.age_range || '',
      height: character_data.height || '',
      character_type: character_data.character_type || '主角',
      token
    });
  }

  // 更新故事角色
  update_story_character (token, character_id, character_data, user_type = 1) {
    return this.request(`${this.apiBase}/character/update`, 'POST', {
      token,
      character_id,
      character_name: character_data.character_name || '',
      character_description: character_data.character_description || '',
      is_public: character_data.is_public || false,
      gender: character_data.gender || '',
      profession: character_data.profession || '',
      style: character_data.style || '',
      clothing: character_data.clothing || '',
      appearance: character_data.appearance || '',
      personality: character_data.personality || '',
      background: character_data.background || '',
      avatar_image: character_data.avatar_image || '',
      full_image: character_data.full_image || '',
      age_range: character_data.age_range || '',
      height: character_data.height || '',
      character_type: character_data.character_type || ''
    });
  }

  // 删除故事角色
  delete_story_character (token, character_id, user_type = 1) {
    return this.request(`${this.apiBase}/character/delete`, 'POST', {
      token,
      character_id
    });
  }

  // 从故事分镜中提取角色
  extract_characters_from_story (token, story_uuid, user_type = 1) {
    return this.request(`${this.apiBase}/story/extract_characters`, 'POST', {
      token,
      story_uuid,
      user_type
    });
  }

  // 提交角色作图任务
  submit_character_image_task (token, character_id, story_uuid, aspect_ratio = '1:1', user_type = 1) {
    return this.request(`${this.apiBase}/character/submit_image_task`, 'POST', {
      character_id,
      aspect_ratio,
      token,
      story_uuid,
      user_type
    });
  }

  // 提交分镜制图任务
  submit_storyboard_image_task (token, storyboard_uuid, aspect_ratio = '1:1', user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/submit_image_task`, 'POST', {
      storyboard_uuid,
      aspect_ratio,
      token,
      user_type
    });
  }

  // 批量提交分镜制图任务
  batch_submit_storyboard_image_task (token, story_uuid, aspect_ratio = '1:1', user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/batch_submit_image_task`, 'POST', {
      story_uuid,
      aspect_ratio,
      token,
      user_type
    });
  }

  // 获取分镜媒体素材库
  get_storyboard_media (token, storyboard_uuid, media_type = null) {
    return this.request(`${this.apiBase}/storyboard/get_media`, 'POST', {
      token,
      storyboard_uuid,
      media_type
    });
  }

  // 设置分镜主展示素材
  set_primary_display_media (token, storyboard_uuid, media_uuid, media_type = 'image') {
    return this.request(`${this.apiBase}/storyboard/set_primary_display_media`, 'POST', {
      token,
      storyboard_uuid,
      media_uuid,
      media_type
    });
  }

  // 通过URL链接添加分镜媒体素材
  add_storyboard_media_by_url (token, storyboard_uuid, media_url, media_type = 'image', options = {}) {
    const params = {
      token,
      storyboard_uuid,
      media_type,
      media_url,
      set_as_primary: options.set_as_primary !== undefined ? options.set_as_primary : true
    };

    // 添加可选参数
    if (options.width) params.width = options.width;
    if (options.height) params.height = options.height;
    if (options.duration) params.duration = options.duration;
    if (options.aspect_ratio) params.aspect_ratio = options.aspect_ratio;
    if (options.thumbnail_url) params.thumbnail_url = options.thumbnail_url;

    // 配音相关字段
    if (options.dialogue_text) params.dialogue_text = options.dialogue_text;
    if (options.character_name) params.character_name = options.character_name;
    if (options.language) params.language = options.language;
    if (options.emotion) params.emotion = options.emotion;
    if (options.voice_id) params.voice_id = options.voice_id;

    return this.request(`${this.apiBase}/storyboard/add_media_by_url`, 'POST', params);
  }


  // 生成故事草稿
  generate_story_draft (story_uuid, workflow_id = '7520072401943855116') {
    const headers = {
      'Authorization': `Bearer pat_shpxcwZ0PUr0oe9m3wN4CQdMuAl72yfvvYUVWB9tE7j7nEyulKJW3T44BzavmFKC`,
      'Content-Type': 'application/json'
    };

    return fetch(`https://api.coze.cn/v1/workflow/run`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        workflow_id,
        parameters: {
          story_id: story_uuid
        }
      })
    }).then(response => response.json());
  }

  // 获取上传凭证
  async getUploadToken () {
    const response = await fetch(`${this.apiBase}/upload-token`, {
      method: 'GET',
      headers: {
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // 获取视频播放链接
  async getVideoPlayUrl (videoId, expireTime = 7200) {
    const response = await fetch(`${this.apiBase}/video/play-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify({
        video_id: videoId,
        expire_time: expireTime
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // 提交分镜视频制作任务
  submit_video_task (token, storyboard_uuid, resolution = '480p', duration = 5, model = 'doubao-seedance-1-0-lite-i2v-250428', user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/submit_video_task`, 'POST', {
      storyboard_uuid,
      resolution,
      duration,
      model,
      token,
      user_type
    });
  }

  // 获取音色列表
  get_voice_list (token, page_num = 1, page_size = 100, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/get_voice_list`, 'POST', {
      token,
      page_num,
      page_size,
      user_type
    });
  }

  // 更新角色音色配置
  update_character_voice (voiceData) {
    return this.request(`${this.apiBase}/character/update`, 'POST', voiceData);
  }

  // 多人音频合成
  multi_voice_tts (token, story_uuid, storyboard_uuid, audio_content, default_voice_id = '7426720361732915209', speed = 1, user_type = 1) {
    return this.request(`${this.apiBase}/storyboard/multi_voice_tts`, 'POST', {
      token,
      story_uuid,
      storyboard_uuid,
      audio_content,
      default_voice_id,
      speed,
      user_type
    });
  }

  // 创建邀请码
  create_invite_code (token, user_type = 1) {
    return this.request(`${this.apiBase}/create_invite_code`, 'POST', {
      token,
      user_type
    });
  }

  // 获取邀请码
  get_invite_code_info (token, user_type = 1) {
    return this.request(`${this.apiBase}/get_invite_code_info`, 'POST', {
      token,
      user_type
    });
  }

  // 更新邀请码
  update_leader (token, leader_id, user_type = 1) {
    return this.request(`${this.apiBase}/update_leader`, 'POST', {
      token,
      user_type,
      leader_id
    });
  }

  // 绑定团长邀请码
  update_leader_by_invite_code (token, invite_code, user_type = 1) {
    return this.request(`${this.apiBase}/update_leader_by_invite_code`, 'POST', {
      token,
      user_type,
      invite_code
    });
  }
  

  // 获取草稿状态
  get_draft_status (draft_url) {
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify({
        draft_url: draft_url
      })
    };

    return fetch(`${this.apiBase}/get_draft_status`, requestOptions)
      .then(response => response.json())
      .catch(error => {
        throw error;
      });
  }

  // 获取草稿模板列表
  get_draft_flow_template_list (token, user_type = 1, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/draft_flow_template/list`, 'POST', {
      token,
      user_type,
      page,
      page_size
    });
  }

  // 添加草稿模板
  add_draft_flow_template (token, user_type = 1, workflow_id, workflow_name, video_url, img_url, request_token, is_public) {
    return this.request(`${this.apiBase}/draft_flow_template/add`, 'POST', {
      token,
      user_type,
      workflow_id,
      workflow_name,
      video_url,
      img_url,
      request_token,
      is_public
    });
  }

  // 删除草稿模板
  delete_draft_flow_template (token, user_type = 1, template_id) {
    return this.request(`${this.apiBase}/draft_flow_template/delete`, 'POST', {
      token,
      user_type,
      template_id
    });
  }

  // 搜索草稿模板
  search_draft_flow_template (token, user_type = 1, workflow_name, page, page_size, only_my_templates) {
    return this.request(`${this.apiBase}/draft_flow_template/search`, 'POST', {
      token,
      user_type,
      workflow_name,
      page,
      page_size,
      only_my_templates
    });
  }

  // 更新草稿模板
  update_draft_flow_template (token, user_type = 1, template_id, workflow_name, video_url, img_url, request_token, is_public) {
    return this.request(`${this.apiBase}/draft_flow_template/update`, 'POST', {
      token,
      user_type,
      template_id,
      workflow_name,
      video_url,
      img_url,
      request_token,
      is_public
    });
  } 

  // Flux Kontext 相关 API 方法
  
  // 提交 Flux Kontext 作图任务
  submit_flux_kontext_task (token, user_type, prompt, input_image, aspect_ratio, task_type) {
    return this.request(`${this.apiBase}/flux_kontext/submit`, 'POST', {
      token,
      user_type,
      prompt,
      input_image,
      aspect_ratio,
      task_type
    });
  }

  // 获取单个 Flux Kontext 任务信息
  get_flux_kontext_task (token, user_type, task_id) {
    return this.request(`${this.apiBase}/flux_kontext/get_task`, 'POST', {
      token,
      user_type,
      task_id
    });
  }

  // 获取 Flux Kontext 任务列表
  get_flux_kontext_task_list (token, user_type, page = 1, page_size = 10) {
    return this.request(`${this.apiBase}/flux_kontext/list_tasks`, 'POST', {
      token,
      user_type,
      page,
      page_size
    });
  }

  // 翻译文本
  translate_text (token, text, user_type = 1) {
    return this.request(`${this.apiBase}/translate`, 'POST', {
      token,
      text,
      user_type
    });
  }

  // AI推理优化提示词（仅sora2模型）
  ai_reasoning (token, text) {
    return this.request(`${this.apiBase}/v2/tasks/create`, 'POST', {
      token,
      task_type: "coze_v2_api",
      app_name: '7579827443582664713',
      input_params: {
        input: text
      }
    });
  }

  // 解锁智能体
  unlock_bot (token,bot_id, user_type = 1) {
    return this.request(`${this.apiBase}/unlock_bot`, 'POST', {
      token,
      bot_id,
      user_type
    });
  }

  // 搜索智能体
  search_bots (token, keyword, page = 1, size = 20, user_type = 1) {
    return this.request(`${this.apiBase}/search_bots`, 'POST', {
      token,
      user_type,
      keyword,
      page,
      size
    });
  }

  // ======================== 工单系统相关接口 ========================
  
  // 工单系统专用请求方法
  async ticketRequest(url, method, data, token) {
    const requestOptions = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      }
    };

    // 工单系统的token通过参数传递，不是header
    const requestData = { ...data };
    if (token) {
      requestData.token = token;
    }

    // 添加请求数据到body
    if (requestData) {
      requestOptions.body = JSON.stringify(requestData);
    }

    try {
      const response = await fetch(url, requestOptions);
      const responseData = await response.json();
      
      if (!response.ok) {
        throw { 
          status: response.status, 
          data: responseData,
          msg: responseData.msg || `HTTP ${response.status} error`
        };
      }
      
      return responseData;
    } catch (error) {
      console.error(`工单API请求失败 ${url}:`, error.message || error);
      
      // 如果是网络错误或JSON解析错误
      if (!error.status) {
        throw {
          code: 500,
          msg: '网络连接失败或服务器错误',
          data: null
        };
      }
      
      if (this._isAuthError(error)) {
        this._handleAuthExpired();
        throw {
          code: error.status || 500,
          msg: '登录已过期',
          data: error.data || null
        };
      }
      
      // 抛出统一格式的错误
      throw {
        code: error.status || 500,
        msg: error.msg || error.message || '请求失败',
        data: error.data || null
      };
    }
  }
  
  // 用户端工单接口
  
  // 提交工单
  async submit_ticket(token, title, content, ticket_type = 1, priority = 2, contact_type = 1, contact_info, attachment_urls = []) {
    return await this.ticketRequest(`${this.apiBase}/submit_ticket`, 'POST', {
      title,
      content,
      ticket_type,
      priority,
      contact_type,
      contact_info,
      attachment_urls
    }, token);
  }

  // 用户回复工单
  async reply_ticket(token, ticket_id, content, attachment_urls = []) {
    return await this.ticketRequest(`${this.apiBase}/reply_ticket`, 'POST', {
      ticket_id,
      content,
      attachment_urls
    }, token);
  }

  // 获取工单详情
  async get_ticket_detail(token, ticket_id) {
    return await this.ticketRequest(`${this.apiBase}/get_ticket_detail`, 'POST', {
      ticket_id
    }, token);
  }

  // 获取用户工单列表
  async get_user_tickets(token, page = 1, size = 10, status = null, ticket_type = null) {
    const data = { page, size };
    if (status !== null) data.status = status;
    if (ticket_type !== null) data.ticket_type = ticket_type;
    
    return await this.ticketRequest(`${this.apiBase}/get_user_tickets`, 'POST', data, token);
  }

  // 获取官方消息通知
  get_official_notification(page = 1, size = 10) {
    return this.request(`${this.apiBase}/get_official_notification`, 'POST', {page, size})
  }

  
      // 获取我的智能体列表
  
  get_my_bots (token, page = 1, size = 10, user_type = 1) {
    return this.request(`${this.apiBase}/get_my_bots`, 'POST', {
      token,
      user_type,

      page,
      size
    });
  }


  // 查询用户管理员权限
  async check_admin_permission(token) {
    return await this.ticketRequest(`${this.apiBase}/check_admin_permission`, 'POST', {}, token);
  }

  // 管理员端工单接口
  
  // 获取所有工单列表（管理员）
  async admin_get_all_tickets(token, page = 1, size = 10, status = null, ticket_type = null, priority = null, user_id = null) {
    const data = { page, size };
    if (status !== null) data.status = status;
    if (ticket_type !== null) data.ticket_type = ticket_type;
    if (priority !== null) data.priority = priority;
    if (user_id !== null) data.user_id = user_id;
    
    return await this.ticketRequest(`${this.apiBase}/admin/get_all_tickets`, 'POST', data, token);
  }

  // 管理员回复工单
  async admin_reply_ticket(token, ticket_id, content, attachment_urls = [], is_internal = false) {
    return await this.ticketRequest(`${this.apiBase}/admin/reply_ticket`, 'POST', {
      ticket_id,
      content,
      attachment_urls,
      is_internal
    }, token);
  }

  // 修改工单状态（管理员）
  async admin_update_ticket_status(token, ticket_id, status, remark = '') {
    return await this.ticketRequest(`${this.apiBase}/admin/update_ticket_status`, 'POST', {
      ticket_id,
      status,
      remark
    }, token);
  }

  // 管理员获取工单详情
  async admin_get_ticket_detail(token, ticket_id) {
    return await this.ticketRequest(`${this.apiBase}/admin/get_ticket_detail`, 'POST', {
      ticket_id
    }, token);
  }

  // 获取工单统计信息（管理员）
  async admin_get_ticket_stats(token) {
    return await this.ticketRequest(`${this.apiBase}/admin/get_ticket_stats`, 'POST', {}, token);
  }

  // 批量操作工单（管理员）
  async admin_batch_update_tickets(token, ticket_ids, action, status = null, admin_id = null, remark = '') {
    const data = { ticket_ids, action, remark };
    if (status !== null) data.status = status;
    if (admin_id !== null) data.admin_id = admin_id;
    
    return await this.ticketRequest(`${this.apiBase}/admin/batch_update_tickets`, 'POST', data, token);
  }

  // 创建智能体
  create_bot (token, botData, user_type = 1) {
    return this.request(`${this.apiBase}/create_bot`, 'POST', {
      token,
      user_type,
      ...botData
    });
  }

  // 更新智能体
  update_bot (token, botData, user_type = 1) {
    return this.request(`${this.apiBase}/update_bot`, 'POST', {
      token,
      user_type,
      ...botData
    });
  }

  // 删除智能体
  delete_bot (token, bot_id, user_type = 1) {
    return this.request(`${this.apiBase}/delete_bot`, 'POST', {
      token,
      user_type,
      bot_id
    });
  }

  // 提交智能体审核
  submit_bot_review (token, bot_id, user_type = 1) {
    return this.request(`${this.apiBase}/submit_bot_review`, 'POST', {
      token,
      user_type,
      bot_id
    });
  }

  // 获取我的智能体详细信息
  get_my_bot_info (token, bot_id, user_type = 1) {
    return this.request(`${this.apiBase}/get_my_bot_info`, 'POST', {
      token,
      user_type,
      bot_id
    });
  }

  // 添加工作流
  add_workflow (token, bot_id, workflowData, user_type = 1) {
    return this.request(`${this.apiBase}/add_workflow`, 'POST', {
      token,
      user_type,
      bot_id,
      ...workflowData
    });
  }

  // 更新工作流
  update_workflow (token, workflowData, user_type = 1) {
    return this.request(`${this.apiBase}/update_workflow`, 'POST', {
      token,
      user_type,
      ...workflowData
    });
  }

  // 删除工作流
  delete_workflow (token, workflow_id, user_type = 1) {
    return this.request(`${this.apiBase}/delete_workflow`, 'POST', {
      token,
      user_type,
      workflow_id
    });
  }

  // 获取创作者收益记录
  get_creator_earnings (token, page = 1, size = 10, bot_id = null, user_type = 1) {
    const params = {
      token,
      user_type,
      page,
      size
    };
    if (bot_id) {
      params.bot_id = bot_id;
    }
    return this.request(`${this.apiBase}/get_creator_earnings`, 'POST', params);
  }

  // ======================== 智能体案例相关接口 ========================
  
  // 新增智能体案例详情
  add_bot_case (token, bot_id, image_url = null, video_url = null, user_type = 1) {
    return this.request(`${this.apiBase}/add_bot_case`, 'POST', {
      token,
      bot_id,
      image_url,
      video_url,
      user_type
    });
  }

  // 获取用户智能体案例列表（需要认证，支持状态筛选）
  get_bot_case_list (token, bot_id, page = 1, page_size = 10, user_type = 1, status = null) {
    const params = {
      token,
      bot_id,
      page,
      page_size,
      user_type
    };
    
    // 只有传递了status参数才添加到请求中
    if (status !== null) {
      params.status = status;
    }
    
    return this.request(`${this.apiBase}/get_bot_case_list`, 'POST', params);
  }

  // 删除智能体案例
  delete_bot_case (token, case_id, user_type = 1) {
    return this.request(`${this.apiBase}/delete_bot_case`, 'POST', {
      token,
      case_id,
      user_type
    });
  }

  // 更新智能体案例
  update_bot_case (token, case_id, image_url = null, video_url = null, user_type = 1) {
    return this.request(`${this.apiBase}/update_bot_case`, 'POST', {
      token,
      case_id,
      image_url,
      video_url,
      user_type
    });
  }


   // 获取服务定价列表
   list_service_pricing (token, user_type = 1, is_active = true) {
    return this.request(`${this.apiBase}/list_service_pricing`, 'POST', {
      token,
      user_type,
      is_active
    });
  }



  // ======================== 用户购买记录相关接口 ========================
  
  // 获取用户购买记录
  get_user_purchases (token, page = 1, size = 10, bot_id = '', settlement_status = -1, user_type = 1) {
    return this.request(`${this.apiBase}/get_user_purchases`, 'POST', {
      token,
      page,
      size,
      bot_id,
      settlement_status,
      user_type
    });
  }

  // ======================== 优惠券相关接口 ========================
  
  // 获取用户优惠券列表
  get_user_coupons(token, status = null, shop_id = null, page = 1, page_size = 10, user_type = 1) {
    const data = { token, page, page_size, user_type };
    if (status !== null) data.status = status; // 只有status不为null时才添加
    if (shop_id !== null) data.shop_id = shop_id; // 只有shop_id不为null时才添加
    
    return this.request(`${this.apiBase}/get_user_coupons`, 'POST', data);
  }

  // 每日派发优惠券
  grant_daily_coupons(token, user_type = 1) {
    return this.request(`${this.apiBase}/grant_daily_coupons`, 'POST', { token, user_type });
  }

  // ======================== Sora2 视频生成相关接口 ========================
  
  /**
   * 简化的图片上传方法 - 上传文件并获取永久地址
   */
  async upload_image_simple(file) {
    try {
      console.log('开始简化图片上传流程，文件:', file.name);
      
      // 1. 获取coze token
      console.log('步骤1: 获取coze token');
      const tokenResponse = await this.get_coze_token();
      console.log('Token响应:', tokenResponse);
      
      if (!tokenResponse || !tokenResponse.upload_key) {
        throw new Error('获取coze token失败');
      }

      const uploadToken = tokenResponse.upload_key;
      const accessToken = tokenResponse.data; // 用于获取永久地址的token
      console.log('上传Token:', uploadToken);

      // 2. 上传图片到coze
      console.log('步骤2: 上传图片到coze');
      const uploadResponse = await this.upload_image_to_coze(file, uploadToken);
      console.log('上传响应:', uploadResponse);
      
      if (!uploadResponse || !uploadResponse.data || !uploadResponse.data.id) {
        throw new Error('上传图片失败');
      }

      const fileId = uploadResponse.data.id;
      console.log('获得文件ID:', fileId);

      // 3. 获取永久访问地址（而不是临时API端点）
      let permanentUrl = '';
      if (accessToken) {
        console.log('步骤3: 获取图片永久地址');
        try {
          const urlResponse = await this.get_image_url_by_file_id(fileId, accessToken);
          console.log('永久地址响应:', urlResponse);
          
          if (urlResponse && urlResponse.data) {
            let parsedData;
            if (typeof urlResponse.data === 'string') {
              parsedData = JSON.parse(urlResponse.data);
            } else {
              parsedData = urlResponse.data;
            }
            
            if (parsedData && parsedData.output) {
              permanentUrl = parsedData.output;
              console.log('获得永久地址:', permanentUrl);
            }
          }
        } catch (urlError) {
          console.warn('获取永久地址失败，将使用文件ID:', urlError.message);
        }
      }
      
      // 如果获取永久地址失败，返回错误（因为临时API端点无法公开访问）
      if (!permanentUrl) {
        throw new Error('无法获取图片永久地址，请重试');
      }
      
      return {
        success: true,
        file_id: fileId,
        temp_url: permanentUrl, // 现在返回的是真正可访问的永久地址
        upload_info: uploadResponse.data
      };
    } catch (error) {
      console.error('简化图片上传流程失败:', error);
      throw error;
    }
  }
  
  /**
   * 创建 Sora2 视频生成任务
   */
  create_sora2_task(token, user_type, prompt, model, img_url = '') {
    const input_params = {
      prompt: prompt,
      model: model
    };
    
    if (img_url && img_url.trim()) {
      input_params.img_url = img_url.trim();
    }
    
    return this.request(`${this.apiBase}/fal/tasks/create`, 'POST', {
      token,
      user_type,
      app_name: 'st-ai/sora2',
      input_params: input_params,
      task_type: 'coze_api'
    });
  }

  /**
   * 查询 Sora2 任务状态
   */
  get_sora2_task_info(task_id) {
    return this.request(`${this.apiBase}/fal/tasks/info`, 'POST', {
      task_id
    });
  }

  /**
   * 获取 Sora2 任务列表
   */
  get_sora2_task_list(token, user_type, page, page_size) {
    return this.request(`${this.apiBase}/fal/tasks/list`, 'POST', {
      token,
      user_type,
      page,
      page_size,
      app_name: '' // 空字符串获取所有模型的任务
    });
  }

  /**
   * 删除 FAL 任务
   */
  delete_fal_task(token, task_id) {
    return this.request(`${this.apiBase}/fal/tasks/delete`, 'POST', {
      token,
      user_type: 1,
      task_id
    });
  }

  /**
   * 获取支持的AI模型列表
   */
  get_ai_models_list(token, user_type) {
    return this.request(`${this.apiBase}/fal/models`, 'POST', {
      token,
      user_type
    });
  }


  // ======================== 通用 FAL 任务（rep_api/图像&视频等） ========================

  /**
   * 创建通用 FAL 任务（用于除 Sora2 以外的模型，例如 VEO3、图片生成等）
   * - app_name: 后端识别的应用名称，例如 'st-ai/veo3_api'、'st-ai/flux_kontext' 等
   * - input_params: 按模型 OpenAPI 定义组织好的参数对象
   * - task_type: 任务类型，默认 'rep_api'；传 null 或 undefined 时不发送该字段
   */
  create_rep_task(token, app_name, input_params = {}, task_type = 'rep_api', user_type = 1) {
    const body = {
      token,
      user_type,
      app_name,
      input_params
    };
    // 只有当 task_type 有值时才添加到请求体
    if (task_type) {
      body.task_type = task_type;
    }
    return this.request(`${this.apiBase}/fal/tasks/create`, 'POST', body);
  }

  /**
   * 计算通用 FAL 任务价格（若后端不支持则由调用方兜底本地估算）
   * - 尝试调用 /fal/tasks/calc_price 接口
   */
  async calculate_rep_price(app_name, input_params = {}, user_type = 1) {
    try {
      // 新定价接口：/get_service_pricing 按 app_name 返回基础价格、VIP倍率等
      const resp = await this.request(`${this.apiBase}/get_service_pricing`, 'POST', {
        app_name
      });
      return resp;
    } catch (err) {
      throw err;
    }
  }

  /**
   * 动态计算任务价格（支持所有模型）
   * - 根据模型和参数实时计算价格
   */
  async calculate_task_price(token, app_name, input_params = {}, user_type = 1) {
    try {
      const response = await this.request(`${this.apiBase}/test_pricing_calculation`, 'POST', {
        token,
        user_type,
        app_name,
        input_params
      });
      return response;
    } catch (error) {
      console.error('动态价格计算失败:', error);
      throw error;
    }
  }

  // ======================== 灵感画布项目管理 API ========================

  /**
   * 创建新项目
   * @param {string} token - 用户token（必填）
   * @param {string} name - 项目名称（必填）
   * @param {string} description - 项目描述（可选）
   * @param {boolean} is_public - 是否公开（可选，默认false）
   * @param {number} user_type - 用户类型（默认1）
   */
  createDrawProject(token, name, description = '', is_public = false, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/`, 'POST', {
      token,
      user_type,
      name,
      description,
      is_public
    });
  }

  /**
   * 获取我的项目列表
   * @param {string} token - 用户token（必填）
   * @param {number} skip - 跳过的记录数（默认0）
   * @param {number} limit - 返回的最大记录数（默认100）
   * @param {number} user_type - 用户类型（默认1）
   */
  getMyDrawProjects(token, skip = 0, limit = 100, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/my?skip=${skip}&limit=${limit}`, 'POST', {
      token,
      user_type
    });
  }

  /**
   * 获取公开项目列表（无需认证）
   * @param {number} skip - 跳过的记录数（默认0）
   * @param {number} limit - 返回的最大记录数（默认100）
   */
  getPublicDrawProjects(skip = 0, limit = 100) {
    return this.request(`${this.apiBase}/v1/projects/public?skip=${skip}&limit=${limit}`, 'POST', {});
  }

  /**
   * 搜索项目
   * @param {string} token - 用户token（必填）
   * @param {string} keyword - 搜索关键词
   * @param {number} skip - 跳过的记录数（默认0）
   * @param {number} limit - 返回的最大记录数（默认100）
   * @param {number} user_type - 用户类型（默认1）
   */
  searchDrawProjects(token, keyword, skip = 0, limit = 100, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/search?q=${encodeURIComponent(keyword)}&skip=${skip}&limit=${limit}`, 'POST', {
      token,
      user_type
    });
  }

  /**
   * 获取项目详情
   * @param {string} token - 用户token（必填）
   * @param {string|number} projectId - 项目ID或UUID
   * @param {number} user_type - 用户类型（默认1）
   */
  getDrawProject(token, projectId, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/${projectId}`, 'POST', {
      token,
      user_type
    });
  }

  /**
   * 更新项目信息
   * @param {string} token - 用户token（必填）
   * @param {string|number} projectId - 项目ID或UUID
   * @param {Object} data - 更新数据 { name?, description?, is_public? }
   * @param {number} user_type - 用户类型（默认1）
   */
  updateDrawProject(token, projectId, data, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/${projectId}/update`, 'POST', {
      token,
      user_type,
      ...data
    });
  }

  /**
   * 删除项目
   * @param {string} token - 用户token（必填）
   * @param {string|number} projectId - 项目ID或UUID
   * @param {number} user_type - 用户类型（默认1）
   */
  deleteDrawProject(token, projectId, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/${projectId}/delete`, 'POST', {
      token,
      user_type
    });
  }

  /**
   * 克隆公开项目
   * @param {string} token - 用户token（必填）
   * @param {string|number} projectId - 项目ID或UUID
   * @param {number} user_type - 用户类型（默认1）
   */
  cloneDrawProject(token, projectId, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/${projectId}/clone`, 'POST', {
      token,
      user_type
    });
  }

  /**
   * 保存画布快照
   * @param {string} token - 用户token（必填）
   * @param {string|number} projectId - 项目ID或UUID
   * @param {Object} snapshot - 画布快照数据
   * @param {number} user_type - 用户类型（默认1）
   */
  saveDrawCanvas(token, projectId, snapshot, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/${projectId}/canvas`, 'POST', {
      token,
      user_type,
      snapshot
    });
  }

  /**
   * 加载画布快照
   * @param {string} token - 用户token（必填）
   * @param {string|number} projectId - 项目ID或UUID
   * @param {number} user_type - 用户类型（默认1）
   */
  loadDrawCanvas(token, projectId, user_type = 1) {
    return this.request(`${this.apiBase}/v1/projects/${projectId}/canvas/load`, 'POST', {
      token,
      user_type
    });
  }

  // ======================== Cloudflare R2 图片上传相关 API ========================

  /**
   * 获取 Cloudflare R2 上传凭证
   * @param {string} fileName - 文件名（必填）
   * @param {string} contentType - 文件MIME类型（必填，如 'image/jpeg'）
   * @param {number} expiresIn - 过期时间（秒，默认3600）
   */
  async getCfR2Token(fileName, contentType, expiresIn = 3600) {
    return this.request(`${this.apiBase}/get_cf_r2_token`, 'POST', {
      file_name: fileName,
      content_type: contentType,
      expires_in: expiresIn
    });
  }

  /**
   * 上传文件到 Cloudflare R2
   * @param {string} uploadUrl - 预签名上传URL
   * @param {File} file - 要上传的文件
   * @param {string} contentType - 文件MIME类型
   */
  async uploadToR2(uploadUrl, file, contentType) {
    try {
      console.log('开始上传文件到R2:', {
        url: uploadUrl,
        fileName: file.name,
        fileSize: file.size,
        contentType: contentType
      });

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType
        },
        body: file
      });

      console.log('R2上传响应状态:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('R2上传失败详情:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`R2上传失败: HTTP ${response.status} - ${errorText || response.statusText}`);
      }

      console.log('R2上传成功');
      return { success: true };
    } catch (error) {
      console.error('上传文件到R2失败:', error);
      throw error;
    }
  }

  /**
   * 完整的图片上传到R2流程
   * @param {File} file - 要上传的图片文件
   * @returns {Promise<{success: boolean, public_url: string, file_key: string}>}
   */
  async uploadImageToR2(file) {
    try {
      console.log('开始完整R2图片上传流程，文件:', file.name);

      // 1. 获取上传凭证
      console.log('步骤1: 获取R2上传凭证');
      const tokenResponse = await this.getCfR2Token(
        file.name,
        file.type,
        3600
      );

      console.log('Token响应:', tokenResponse);

      if (!tokenResponse || tokenResponse.code !== 200 || !tokenResponse.data) {
        throw new Error(tokenResponse?.msg || '获取上传凭证失败');
      }

      const { upload_url, public_url, file_key, content_type } = tokenResponse.data;

      if (!upload_url || !public_url) {
        throw new Error('上传凭证数据不完整');
      }

      // 2. 上传文件到R2
      console.log('步骤2: 上传文件到R2');
      await this.uploadToR2(upload_url, file, content_type || file.type);

      console.log('步骤3: 上传完成，返回公开URL:', public_url);

      return {
        success: true,
        public_url,
        file_key,
        content_type: content_type || file.type
      };
    } catch (error) {
      console.error('完整R2图片上传流程失败:', error);
      throw error;
    }
  }

  /**
   * 通用文件上传方法（支持图片和视频）
   * @param {FormData} formData - 包含文件和token的FormData对象
   * @returns {Promise<{code: number, data: {url: string}, msg?: string}>}
   */
  async upload_file(formData) {
    // 使用R2上传流程
    const file = formData.get('file');
    if (!file) {
      throw new Error('没有找到要上传的文件');
    }

    try {
      // 判断文件类型，使用对应的上传方法
      if (file.type.startsWith('image/')) {
        const result = await this.uploadImageToR2(file);
        return {
          code: 200,
          data: {
            url: result.public_url
          }
        };
      } else if (file.type.startsWith('video/')) {
        // 对于视频文件，也使用R2上传（假设支持）
        const tokenResponse = await this.getCfR2Token(
          file.name,
          file.type,
          3600
        );

        if (!tokenResponse || tokenResponse.code !== 200 || !tokenResponse.data) {
          throw new Error(tokenResponse?.msg || '获取上传凭证失败');
        }

        const { upload_url, public_url, file_key, content_type } = tokenResponse.data;

        if (!upload_url || !public_url) {
          throw new Error('上传凭证数据不完整');
        }

        await this.uploadToR2(upload_url, file, content_type || file.type);

        return {
          code: 200,
          data: {
            url: public_url
          }
        };
      } else {
        throw new Error('不支持的文件类型');
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      throw error;
    }
  }

  // ======================== 豆包视频生成 API ========================

  /**
   * 创建豆包视频生成任务（图生视频）
   * @param {string} token - 用户token
   * @param {string} imageUrl - 图片URL（必须是公开可访问的 HTTPS 链接）
   * @param {string} prompt - 视频描述提示词
   * @param {string} resolution - 分辨率：'480p' | '720p' | '1080p'
   * @param {number} duration - 视频时长（秒）：5 或 10
   * @param {string} aspectRatio - 宽高比：'16:9' | '9:16' | '1:1'
   * @param {boolean} cameraFixed - 是否固定镜头
   * @param {number} user_type - 用户类型（默认1）
   */
  create_doubao_video_task(
    token, 
    imageUrl, 
    prompt, 
    resolution = '720p', 
    duration = 5, 
    aspectRatio = '16:9',
    cameraFixed = false,
    user_type = 1
  ) {
    // 构建完整的提示词文本（包含所有参数）
    const fullPromptText = `${prompt} --dur ${duration} --rs ${resolution} --rt ${aspectRatio} --cf ${cameraFixed} --wm false`;
    
    // 构建 content 数组
    const content = [
      {
        type: 'text',
        text: fullPromptText
      },
      {
        type: 'image_url',
        image_url: {
          url: imageUrl
        }
      }
    ];

    return this.request(`${this.apiBase}/fal/tasks/create`, 'POST', {
      token,
      user_type,
      app_name: 'st-ai/doubao-seedance-1-0-pro-fast-251015',
      input_params: {
        resolution,
        duration,
        content
      },
      task_type: 'coze_api'
    });
  }

  // ======================== Sora2 视频生成 API ========================

  /**
   * 创建 Sora2 视频生成任务（用于真人风格）
   * @param {string} token - 用户token
   * @param {string} imageUrl - 参考图片URL
   * @param {string} prompt - 视频生成提示词
   * @param {string} model - 模型名称，默认 "sora2-portrait"
   * @returns {Promise}
   */
  create_sora2_video_task(
    token,
    imageUrl = null, // 可选参数，纯文字生成视频时可不传
    prompt,
    model = 'sora2-portrait'
  ) {
    const input_params = {
      prompt,
      model
    };
    
    // 只有当 imageUrl 存在时才添加 image_url 参数
    if (imageUrl) {
      input_params.image_url = imageUrl;
    }
    
    return this.request(`${this.apiBase}/v2/tasks/create`, 'POST', {
      token,
      app_name: 'sora2/video',
      input_params
    });
  }

  /**
   * 查询 Sora2 视频任务状态
   * @param {string} task_id - 任务ID
   * @returns {Promise}
   */
  get_sora2_video_task_info(task_id) {
    return this.request(`${this.apiBase}/v2/tasks/info`, 'POST', {
      task_id
    });
  }

  /**
   * 通过视频任务ID创建角色（真人风格）
   * @param {string} token - 用户token
   * @param {string} videoTaskId - 视频任务ID (UUID格式)
   * @param {string} displayName - 角色名称
   * @param {string} profileImageUrl - 角色头像URL
   * @param {number} categoryId - 分类ID
   * @param {number} isPublic - 是否公开
   * @param {string} timestamps - 视频片段 "start,end"
   * @returns {Promise}
   */
  create_role_by_video(
    token,
    videoTaskId,
    displayName,
    profileImageUrl = '',
    categoryId = 0,
    isPublic = 1,
    timestamps = "0,3"
  ) {
    return this.request(`${this.apiBase}/character/role/create_by_video`, 'POST', {
      token,
      video_task_id: videoTaskId,
      display_name: displayName,
      profile_image_url: profileImageUrl,
      category_id: categoryId,
      is_public: isPublic,
      timestamps: timestamps
    });
  }

  // ======================== 即梦4.0 白底图生成 API ========================

  /**
   * 创建即梦4.0图片生成任务
   * 支持两种模式：
   * 1. 基于已有图片生成白底图（传入 imageUrl）
   * 2. 根据提示词直接生成图片（不传 imageUrl，只传 promptOverride）
   * @param {string} token
   * @param {string} imageUrl - 原始图片的公开URL（可选）
   * @param {string} promptOverride - 可选，自定义提示词
   * @param {number} user_type
   */
  create_seedream_white_background_task(
    token,
    imageUrl = null,
    promptOverride = '',
    user_type = 1
  ) {
    const defaultPrompt =
      '帮我生成一下图中人物的正面视角全身图（从头到鞋子），白色底色，双手下垂，手里无东西';

    const inputParams = {
      prompt: promptOverride || defaultPrompt,
      image_size: {
        width: 1080,
        height: 1920
      },  // 9:16 竖屏，参考 story-models.json 即梦4.0
      num_images: 1,
      max_images: 1,
    };

    // 根据是否有图片选择不同的模型
    // 有图片：使用 edit 模型（图生图）
    // 没有图片：使用 text-to-image 模型（文生图）
    let appName = 'fal-ai/bytedance/seedream/v4/text-to-image';
    
    if (imageUrl) {
      inputParams.image_urls = [imageUrl];
      appName = 'fal-ai/bytedance/seedream/v4/edit';
    }

    return this.request(`${this.apiBase}/fal/tasks/create`, 'POST', {
      token,
      user_type,
      app_name: appName,
      input_params: inputParams
    });
  }

  /**
   * 查询即梦4.0任务状态
   * @param {string} task_id
   */
  get_seedream_task_info(task_id) {
    return this.request(`${this.apiBase}/fal/tasks/info`, 'POST', {
      task_id
    });
  }

  /**
   * 查询豆包视频生成任务状态
   * @param {string} task_id - 任务ID
   */
  get_doubao_video_task_info(task_id) {
    return this.request(`${this.apiBase}/fal/tasks/info`, 'POST', {
      task_id
    });
  }

  /**
   * 获取豆包任务列表
   * @param {string} token - 用户token
   * @param {number} page - 页码
   * @param {number} page_size - 每页数量
   * @param {number} user_type - 用户类型
   */
  get_doubao_video_task_list(token, page = 1, page_size = 10, user_type = 1) {
    return this.request(`${this.apiBase}/fal/tasks/list`, 'POST', {
      token,
      user_type,
      page,
      page_size,
      app_name: 'st-ai/doubao-seedance-1-0-pro-fast-251015'
    });
  }

  // ======================== 故事项目管理 API (Story Script) ========================

  // 创建故事项目
  createStoryProject(token, title, story_content, description = '', style = '', atmosphere = '', aspect_ratio = '16:9', status = 0) {
    return this.request(`${this.apiBase}/story_project/create`, 'POST', {
      token,
      user_type: 1,
      title,
      story_content,
      description,
      style,
      atmosphere,
      aspect_ratio,
      status
    });
  }

  // 获取故事项目列表
  getStoryProjects(token, page = 1, page_size = 10, status = null, keyword = '') {
    const params = { token, user_type: 1, page, page_size };
    if (status !== null) params.status = status;
    if (keyword) params.keyword = keyword;
    return this.request(`${this.apiBase}/story_project/list`, 'POST', params);
  }

  // 获取公开故事项目列表
  getPublicStoryProjects(token, page = 1, page_size = 10, status = null, keyword = '') {
    const params = { token, user_type: 1, page, page_size };
    if (status !== null) params.status = status;
    if (keyword) params.keyword = keyword;
    return this.request(`${this.apiBase}/story_project/public_list`, 'POST', params);
  }

  // 获取故事项目详情
  getStoryProjectDetail(token, project_id) {
    return this.request(`${this.apiBase}/story_project/detail`, 'POST', { token, user_type: 1, project_id });
  }

  // 更新故事项目
  updateStoryProject(token, project_id, updateData) {
    return this.request(`${this.apiBase}/story_project/update`, 'POST', {
      token,
      user_type: 1,
      project_id,
      ...updateData
    });
  }

  // 删除故事项目
  deleteStoryProject(token, project_id) {
    return this.request(`${this.apiBase}/story_project/delete`, 'POST', { token, user_type: 1, project_id });
  }

  // 批量删除故事项目
  batchDeleteStoryProjects(token, project_ids) {
    return this.request(`${this.apiBase}/story_project/batch_delete`, 'POST', { token, user_type: 1, project_ids });
  }

  // 恢复已删除的故事项目
  restoreStoryProject(token, project_id) {
    return this.request(`${this.apiBase}/story_project/restore`, 'POST', { token, user_type: 1, project_id });
  }

  // 获取故事项目统计概览
  getStoryStatistics(token) {
    return this.request(`${this.apiBase}/story_project/statistics/overview`, 'POST', { token, user_type: 1 });
  }

  // 处理指定故事项目
  processStoryProject(token, project_id) {
    return this.request(`${this.apiBase}/story_project/process`, 'POST', { token, user_type: 1, project_id });
  }

  // 批量处理待处理的故事项目
  batchProcessStoryProjects(token, limit = 5) {
    return this.request(`${this.apiBase}/story_project/batch_process`, 'POST', { token, user_type: 1, limit });
  }

  // 获取故事处理状态统计
  getStoryProcessingStatus(token) {
    return this.request(`${this.apiBase}/story_project/processing_status`, 'POST', { token, user_type: 1 });
  }

  // 创建共享项目副本
  createSharedProjectCopy(token, source_project_id, copy_title) {
    return this.request(`${this.apiBase}/story_project/copy`, 'POST', {
      token,
      user_type: 1,
      source_project_id,
      copy_title
    });
  }

  // 获取项目所有媒体资源
  getStoryProjectAllMedia(token, project_id) {
    return this.request(`${this.apiBase}/story_project/media/all`, 'POST', {
      token,
      user_type: 1,
      project_id: parseInt(project_id, 10)
    });
  }

  // ======================== 故事角色管理 API ========================

  // 获取故事角色列表
  getStoryCharacters(token, story_id, page = 1, page_size = 50) {
    return this.request(`${this.apiBase}/story_character/list`, 'POST', {
      token,
      user_type: 1,
      story_id,
      page,
      page_size
    });
  }

  // 创建故事角色
  createStoryCharacter(token, characterData) {
    return this.request(`${this.apiBase}/story_character/create`, 'POST', { token, user_type: 1, ...characterData });
  }

  // 获取角色详情
  getStoryCharacterDetail(token, character_id) {
    return this.request(`${this.apiBase}/story_character/detail`, 'POST', { token, user_type: 1, character_id });
  }

  // 更新角色信息
  updateStoryCharacter(token, character_id, updateData) {
    return this.request(`${this.apiBase}/story_character/update`, 'POST', {
      token,
      user_type: 1,
      character_id,
      ...updateData
    });
  }

  // 删除角色
  deleteStoryCharacter(token, character_id) {
    return this.request(`${this.apiBase}/story_character/delete`, 'POST', { token, user_type: 1, character_id });
  }

  // 批量删除角色
  batchDeleteStoryCharacters(token, character_ids) {
    return this.request(`${this.apiBase}/story_character/batch_delete`, 'POST', { token, user_type: 1, character_ids });
  }

  // 为角色生成图片
  drawStoryCharacter(token, character_id, app_name, input_params) {
    return this.request(`${this.apiBase}/character/draw`, 'POST', {
      token,
      user_type: 1,
      character_id,
      app_name,
      input_params
    });
  }

  // 获取角色图片列表
  getStoryCharacterImages(token, character_id, page = 1, page_size = 20) {
    return this.request(`${this.apiBase}/character/images`, 'POST', {
      token,
      user_type: 1,
      character_id,
      page,
      page_size
    });
  }

  // 选择角色当前图片
  selectStoryCharacterImage(token, character_id, image_task_id) {
    return this.request(`${this.apiBase}/character/select_image`, 'POST', {
      token,
      user_type: 1,
      character_id,
      image_task_id
    });
  }

  // 上传角色图片
  uploadCharacterImage(token, character_id, image_url, original_filename, file_size) {
    return this.request(`${this.apiBase}/character/upload_image`, 'POST', {
      token,
      user_type: 1,
      character_id,
      image_url,
      original_filename,
      file_size
    });
  }

  // ======================== 故事场景管理 API ========================

  // 获取故事场景列表
  getStoryScenes(token, story_id, page = 1, page_size = 100) {
    return this.request(`${this.apiBase}/story_scene/list`, 'POST', {
      token,
      user_type: 1,
      story_id,
      page,
      page_size
    });
  }

  // 获取场景详情
  getStorySceneDetail(token, scene_id) {
    return this.request(`${this.apiBase}/story_scene/detail`, 'POST', { token, user_type: 1, scene_id });
  }

  // 更新场景信息
  updateStoryScene(token, scene_id, updateData) {
    const requestData = { token, user_type: 1, scene_id };
    const allowedFields = [
      'scene_number', 'characters', 'time', 'location', 'visual_description',
      'dialogue', 'narration', 'art_prompt', 'current_image_id', 'current_image_url',
      'current_video_id', 'current_video_url', 'current_audio_id', 'current_audio_url',
      'shot_type', 'camera_angle', 'camera_movement', 'video_prompt', 'audio_prompt', 'atmosphere'
    ];
    allowedFields.forEach(field => {
      if (updateData[field] !== undefined) {
        requestData[field] = updateData[field];
      }
    });
    return this.request(`${this.apiBase}/story_scene/update`, 'POST', requestData);
  }

  // 删除场景
  deleteStoryScene(token, scene_id, auto_reorder = true) {
    return this.request(`${this.apiBase}/story_scene/delete`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      auto_reorder
    });
  }

  // 插入场景
  insertStoryScene(token, sceneData) {
    const requestData = { token, user_type: 1 };
    const requiredFields = ['story_id', 'insert_position'];
    requiredFields.forEach(field => {
      if (sceneData[field] !== undefined) {
        requestData[field] = sceneData[field];
      }
    });
    if (sceneData.insert_position !== 'end' && sceneData.reference_scene_number !== undefined) {
      requestData.reference_scene_number = sceneData.reference_scene_number;
    }
    const allowedOptionalFields = ['characters', 'time', 'location', 'visual_description', 'dialogue', 'narration'];
    allowedOptionalFields.forEach(field => {
      if (sceneData[field] !== undefined && sceneData[field] !== '') {
        requestData[field] = sceneData[field];
      }
    });
    return this.request(`${this.apiBase}/story_scene/insert`, 'POST', requestData);
  }

  // ======================== 场景媒体管理 API ========================

  // 为场景生成媒体（通用接口）
  createStorySceneMedia(token, scene_id, app_name, input_params, media_type) {
    return this.request(`${this.apiBase}/scene/media/create`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      app_name,
      input_params,
      media_type
    });
  }

  // 为场景生成图片（便捷接口）
  createStorySceneImage(token, scene_id, app_name, input_params, media_type = 'image') {
    return this.request(`${this.apiBase}/scene/image/create`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      app_name,
      input_params,
      media_type
    });
  }

  // 为场景生成视频（便捷接口）
  createStorySceneVideo(token, scene_id, app_name, input_params) {
    return this.request(`${this.apiBase}/scene/video/create`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      app_name,
      input_params,
      media_type: 'video',
      task_type: 'coze_api'
    });
  }

  // 为场景生成音频（便捷接口）
  createStorySceneAudio(token, scene_id, app_name, input_params) {
    return this.request(`${this.apiBase}/scene/audio/create`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      app_name,
      media_type: 'audio',
      input_params
    });
  }

  // 获取场景媒体列表
  getStorySceneMediaList(token, scene_id, media_type = null, page = 1, page_size = 20) {
    const params = { token, user_type: 1, scene_id, page, page_size };
    if (media_type) params.media_type = media_type;
    return this.request(`${this.apiBase}/scene/media/list`, 'POST', params);
  }

  // 获取场景图片列表（便捷接口）
  getStorySceneImages(token, scene_id, page = 1, page_size = 20) {
    return this.request(`${this.apiBase}/scene/images`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      page,
      page_size
    });
  }

  // 获取场景视频列表（便捷接口）
  getStorySceneVideos(token, scene_id, page = 1, page_size = 20) {
    return this.request(`${this.apiBase}/scene/videos`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      page,
      page_size
    });
  }

  // 获取场景音频列表（便捷接口）
  getStorySceneAudios(token, scene_id, page = 1, page_size = 20) {
    return this.request(`${this.apiBase}/scene/audios`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      page,
      page_size
    });
  }

  // 选择场景当前音频
  selectStorySceneAudio(token, scene_id, audio_task_id) {
    return this.request(`${this.apiBase}/scene/select_audio`, 'POST', {
      token,
      user_type: 1,
      scene_id,
      audio_task_id
    });
  }

  // 用户上传场景媒体
  uploadStorySceneMedia(token, scene_id, media_type, media_url, original_filename, duration = null, file_size = null, resolution = null) {
    const params = {
      token,
      user_type: 1,
      scene_id,
      media_type,
      media_url,
      original_filename
    };
    if (duration !== null) params.duration = duration;
    if (file_size !== null) params.file_size = file_size;
    if (resolution !== null) params.resolution = resolution;
    return this.request(`${this.apiBase}/scene/media/upload`, 'POST', params);
  }

  // 批量为场景生成媒体（统一接口）
  batchCreateSceneMedia(token, app_name, media_type, tasks) {
    return this.request(`${this.apiBase}/scene/media/batch_create`, 'POST', {
      token,
      user_type: 1,
      app_name,
      media_type,
      tasks
    });
  }

  // 批量为场景生成图片（便捷接口）
  batchCreateSceneImages(token, app_name, tasks) {
    return this.request(`${this.apiBase}/scene/images/batch_create`, 'POST', {
      token,
      user_type: 1,
      app_name,
      tasks
    });
  }

  // 批量为场景生成视频（便捷接口）
  batchCreateSceneVideos(token, app_name, tasks) {
    return this.request(`${this.apiBase}/scene/videos/batch_create`, 'POST', {
      token,
      user_type: 1,
      app_name,
      tasks
    });
  }

  // 批量为场景生成音频（便捷接口）
  batchCreateSceneAudios(token, app_name, tasks) {
    return this.request(`${this.apiBase}/scene/audios/batch_create`, 'POST', {
      token,
      user_type: 1,
      app_name,
      tasks
    });
  }

  // ======================== 语音相关 API ========================

  // 声音设计
  createVoiceDesign(token, prompt, preview_text) {
    return this.request(`${this.apiBase}/voice/design`, 'POST', {
      token,
      user_type: 1,
      prompt,
      preview_text
    });
  }

  // 声音克隆
  createVoiceClone(token, audio_url, text) {
    return this.request(`${this.apiBase}/voice/clone`, 'POST', {
      token,
      user_type: 1,
      audio_url,
      text
    });
  }

  // 获取语音列表
  getVoiceList(token, only_own = false) {
    return this.request(`${this.apiBase}/voice/list`, 'POST', {
      token,
      user_type: 1,
      only_own
    });
  }

  // 获取服务价格
  getServicePricing(token, app_name) {
    return this.request(`${this.apiBase}/get_service_pricing`, 'POST', {
      token,
      user_type: 1,
      app_name
    });
  }

  // 获取服务价格列表
  listServicePricing(token, is_active = true) {
    return this.request(`${this.apiBase}/list_service_pricing`, 'POST', {
      token,
      user_type: 1,
      is_active
    });
  }

  // 提取视频帧
  extractVideoFrame(token, video_url, frame_type = 'last') {
    return this.request(`${this.apiBase}/video/extract_frame`, 'POST', {
      token,
      user_type: 1,
      video_url,
      frame_type
    });
  }

  // ========== Google OAuth ==========

  // 获取 Google OAuth 授权 URL
  getGoogleAuthUrl(ref_code = '', ref = '', redirect_uri = '', client_source = '') {
    const params = new URLSearchParams();
    if (ref_code) {
      params.set('ref_code', ref_code);
    } else if (ref) {
      params.set('ref', ref);
    }
    if (redirect_uri) {
      params.set('redirect_uri', redirect_uri);
    }
    if (client_source) {
      params.set('client_source', client_source);
    }
    const query = params.toString();
    const url = query ? `${this.apiBase}/auth/google/url?${query}` : `${this.apiBase}/auth/google/url`;
    return this.request(url, 'GET');
  }

  // 处理 Google OAuth 回调（用 code 换取用户信息）
  googleCallback(code, state = '', ref_code = '', ref = '', redirect_uri = '') {
    return this.request(`${this.apiBase}/auth/google/callback`, 'POST', {
      code,
      state,
      ref_code,
      ref,
      redirect_uri
    });
  }

}

export default ApiService;
